/**
 * Spend map — see WHERE the money goes. Full-screen overlay: OpenStreetMap
 * via Leaflet (no API key), one pin per place sized by this month's spend
 * there. The side panel lists unplaced expenses; pick one and AI resolves the
 * merchant into real map candidates (Gemini query + Google Geocoding), or
 * click anywhere on the map to drop the pin yourself. Assigning can stick to
 * just this month's item or also to its recurring twin, so next month lands
 * on the map by itself.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { Component, useEffect, useRef, useState } from 'react';
import { fmtKm, withinNearby } from '../distance.js';
import { createPortal } from 'react-dom';
import DetailCard from './DetailCard.jsx';
import CadenceControls from './CadenceControls.jsx';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PinPreview from './PinPreview.jsx';
import { api } from '../api.js';
import { ask } from './Ask.jsx';
import { fmtR, moneySymbol } from '../money.js';
import { taxPackAvailable } from '../global.js';
import VoiceButton from './VoiceButton.jsx';
import SearchField from './SearchField.jsx';
import { useAccordion } from './AccordionRegister.jsx';
import CashSpend from './CashSpend.jsx';

const JHB = [-26.2041, 28.0473];   // sensible SA default centre

/** NEVER a white screen: any render error inside the map surfaces as an
 * inline message with the ? reporter still reachable — not a blank page. */
class MapBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }

  static getDerivedStateFromError(err) { return { err }; }

  render() {
    if (this.state.err) {
      return (
        <div className="p-6 text-sm text-red-400">
          The map hit an error: {String(this.state.err?.message || this.state.err)}
          <span className="block text-slate-400 mt-2">
            Close the map and reopen it — and please report this via the ? button
            so it gets fixed for good.
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SpendMap({ budget, banks = [], onClose, onChanged, openItem = null }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const placeListRef = useRef(null);   // 'by place' list — focus-scroll target
  const posRef = useRef(null);   // user's live position — anchors AI + geocoding
  // Metres from where I am to a place — powers closest-first ordering of the
  // shop pickers (parity with mobile + watch). Infinity when we have no fix or
  // the place has no coords, so located shops always sort ahead.
  const distToPlace = (p) => {
    const me = posRef.current;
    if (!me || p == null || p.lat == null || p.lng == null) return Infinity;
    const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(Number(p.lat) - me.lat), dLng = toRad(Number(p.lng) - me.lng);
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(me.lat)) * Math.cos(toRad(Number(p.lat))) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };
  const hoverRef = useRef(null); // temp map flag shown while hovering a result
  // Bug fix (live): the map must ALWAYS open centred on WHERE YOU ARE. hasFixRef
  // records that a geolocation fix has centred the map; once it has, the pin
  // redraw must NEVER fitBounds over it (that reframed the map onto the cluster
  // of saved pins — the "default centre is wrong" report). didAutoFitRef lets us
  // frame the pins ONCE, and only as the fallback when no fix is available.
  const hasFixRef = useRef(false);
  const didAutoFitRef = useRef(false);
  const markersRef = useRef(new Map());  // place.id -> { marker, label } for pin labels
  const [places, setPlaces] = useState([]);
  const [syncing, setSyncing] = useState(false);  // "Sync locations" busy flag
  const [assigning, setAssigning] = useState(null);   // expense being placed
  const [assignCtx, setAssignCtx] = useState(null);   // shop it was opened under
  const [trip, setTrip] = useState(null);             // active tracked trip
  const [trips, setTrips] = useState([]);             // completed trips, to replay the route (#306)
  const [tripsOpen, setTripsOpen] = useState(false);  // trips list panel open
  const [selTrip, setSelTrip] = useState(null);       // trip whose road is highlighted + zoomed
  // Paid-ride card (Spend Map "Track a trip" → saved): a finished trip can be
  // marked a paid ride (taxi / e-hailing) whose fare logs as a real Transport
  // expense. The route stays hidden on the map until the owner asks to see it,
  // matching the mockup ("hidden until you tap the ride, then it draws").
  const [savedTrip, setSavedTrip] = useState(null);   // just-ended trip → the card
  const [rideMode, setRideMode] = useState(false);    // is this a paid ride?
  const [rideFare, setRideFare] = useState('');       // the fare to log
  const [rideItem, setRideItem] = useState('');       // which transport expense it settles onto
  const [rideBusy, setRideBusy] = useState(false);    // saving the trip end
  const [routeShown, setRouteShown] = useState(false); // draw the selected trip's road?
  const tripRoutes = useRef(null);                    // layerGroup of past-trip polylines
  const tripWatch = useRef(null);
  const tripLine = useRef(null);
  const tripPts = useRef([]);
  const [candidates, setCandidates] = useState(null); // AI/geocode results
  const [alsoRecurring, setAlsoRecurring] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dropMode, setDropMode] = useState(false);
  const [moving, setMoving] = useState(null);    // place being re-located
  const [linking, setLinking] = useState(null);  // fresh place awaiting its expense
  const [linkItem, setLinkItem] = useState(null); // the expense chosen for it
  const [pendingPlace, setPendingPlace] = useState(null); // assign: picked, awaiting confirm
  const [editPlace, setEditPlace] = useState(null);       // place opened for full editing
  const [mergeFrom, setMergeFrom] = useState(null);       // #323 place being merged into another
  const [mergeQ, setMergeQ] = useState('');
  // Place rows ride the SHARED accordion register (Pass 6: one PlaceSheet/row
  // open at a time, crossing the dashboard/manage sections). Key 'place:<id>'.
  const acc = useAccordion();
  const openList = acc.openKey && acc.openKey.startsWith('place:') ? Number(acc.openKey.slice(6)) : null;
  const [cashPlace, setCashPlace] = useState(null);   // place id being cash-spent (null = closed)
  const [mapSort, setMapSort] = useState('amount');   // place list sort: amount | distance | name (Design spec)
  const [areaLabel, setAreaLabel] = useState('');   // 'area in view' legend (reverse-geocoded on pan)
  const [aMode, setAMode] = useState('home');    // 'home' constant | 'visit' dated spend
  const [oTab, setOTab] = useState('basic');    // expense editor tabs: basic | advanced | logged
  const [vAmt, setVAmt] = useState('');          // visit: rand amount
  const [bSpend, setBSpend] = useState('');      // Basic: quick amount-spent log
  const [spendNote, setSpendNote] = useState('');  // optional description on a logged spend
  const [vUnits, setVUnits] = useState('');      // visit: unit count (formula items)
  const [vDate, setVDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [vTimes, setVTimes] = useState('1');   // create: times a day
  const [byDate, setByDate] = useState('');      // 🗓 replay date ('' = whole month)
  const [byView, setByView] = useState('shop');  // day view: shop | expense | all
  const [dayExpense, setDayExpense] = useState(null); // day: expense opened
  const [lens, setLens] = useState('spent');     // whole-month lens: spent | coming (upcoming/still-to-pay)
  const [mapPins, setMapPins] = useState('all');  // 'all' every place | 'linked' only places with a linked expense/event/visit
  // Bank scope — PER SURFACE view state (never synced): a chip narrows the pins
  // and both lists to one account, exactly like the dashboard's bank chips.
  // 0 = All banks. Not persisted to BudgetSettings, unlike lens/range/pins.
  const [bankFilter, setBankFilter] = useState(0);
  const [liveFx, setLiveFx] = useState('');      // Advanced: live formula/amount read-back as you type
  const [deTab, setDeTab] = useState('visits');       // its tab: visits | setup
  const [deUnits, setDeUnits] = useState('');         // add-visit units
  const [deAmt, setDeAmt] = useState('');             // add-visit amount
  const [visits, setVisits] = useState([]);
  const [monthVisits, setMonthVisits] = useState([]);  // whole-month, for place totals
  const [vRepeat, setVRepeat] = useState('once'); // once | daily | weekly | monthly
  const [vDow, setVDow] = useState(String(((new Date().getDay() + 6) % 7) + 1)); // #87 ISO weekday 1=Mon..7=Sun
  // Map events use the SAME cadence control as recurring (shared CadenceControls).
  const [mapCad, setMapCad] = useState({ cadence: 'none', days_of_week: '', times_per_day: 1, expected_day: '', unit_rate: '' });
  const [mapGeo, setMapGeo] = useState(false);
  const [rules, setRules] = useState([]);         // recurring shop events
  const [editRule, setEditRule] = useState(null); // event's overlay editor
  const [editCount, setEditCount] = useState(false); // tap-to-type the unit count
  const [evMore, setEvMore] = useState(false); // event editor: rare controls (cadence detail, per-visit & shop, tax)
  const [erQ, setErQ] = useState('');             // shop search inside it
  const [lq, setLq] = useState('');               // search inside expense pickers
  const [newHereTitle, setNewHereTitle] = useState('');  // "add new expense here" name
  const [newHereAmt, setNewHereAmt] = useState('');      // "add new expense here" amount
  const [q, setQ] = useState('');   // search-as-you-type over both lists

  // A formula item's unit rate — lets a visit be logged in UNITS.
  // What a saved pin IS, for the labels: its stored kind, else inferred (a name
  // starting with a number reads as an address).
  const placeKind = (p) => p.kind
    || (/^\d/.test((p.name || '').trim()) ? 'address' : 'shop');
  const unitOf = (item) => {
    // Per-unit RATE = the LARGER factor of the FIRST rate×count term ONLY.
    // Taking the max across the WHOLE formula wrongly picked up a trailing flat
    // term (600*2+1700 read as R1700 instead of R600); scoping to the first
    // product term fixes that while staying order-independent inside the term,
    // so 8*400 and 400*8 both still give R400.
    const calc = String(item?.calculation || '').replace(/[×]/g, '*');
    const term = calc.split('+').map((t) => t.trim()).find((t) => t.includes('*'));
    if (!term) return 0;
    const nums = term.match(/\d+(?:\.\d+)?/g) || [];
    return nums.length ? Math.max(...nums.map(Number)) : 0;
  };

  // A dated spend AT a place: recorded as a visit AND added to the expense
  // (units advance the paid counter, money adds to spent) — per day, per
  // location, as many times a month as life happens.
  const applyVisit = async (place, item) => {
    const unit = unitOf(item);
    const u = Number(vUnits) || 0;
    const per = Number(vAmt) || 0;          // units set → R PER UNIT (blank = the formula
                                            // rate); no units → a flat R amount.
    const override = u > 0 && per > 0;      // a custom per-unit price for these units
    const amt = u > 0 ? u * (per || unit) : per;
    if (!amt) { setError('Give the visit an amount — rands, or units.'); return false; }
    // A plain unit log advances the formula PAID COUNTER (the backend prices
    // reduce_n at the expense's own rate). A custom per-unit price can't ride
    // that counter — the server prices reduce_n at the formula rate and reverses
    // a visit by units OR money, never both — so an override is stored as a MONEY
    // spend of units × price: exact, and it reverses cleanly on delete.
    const logUnits = (u > 0 && !override) ? u : 0;
    await api('/budget/place-visits/',
              { method: 'POST',
                body: { place: place.id, item: item.id, title: item.title,
                        amount: amt.toFixed(2), units: logUnits,
                        on: vDate, bump: 1, confirmed: true } });   // atomic apply
    const cad = mapGeo ? 'geo' : (mapCad.cadence === 'none' ? 'once' : mapCad.cadence);
    if (cad !== 'once') {
      // Standing shop event from here — the SAME cadence control as recurring
      // (Daily x times / Weekly on which days / Monthly on a date), plus GPS.
      const dow = mapCad.days_of_week || '';
      const rule = await api('/budget/place-visit-rules/',
                             { method: 'POST',
                               body: { place: place.id, item_title: item.title,
                                       units: logUnits,
                                       amount: logUnits ? 0 : amt.toFixed(2),
                                       cadence: cad,
                                       times_per_day: cad === 'daily'
                                         ? Math.max(1, Number(mapCad.times_per_day) || 1) : 1,
                                       day_of_month: cad === 'monthly'
                                         ? Math.min(Number(mapCad.expected_day) || Number(vDate.slice(8, 10)) || 1, 28)
                                         : null,
                                       days_of_week: cad === 'weekly' ? dow : '',
                                       day_of_week: cad === 'weekly'
                                         ? (Number(dow.split(',')[0]) || 1) : null } }).catch(() => null);
      if (rule) setRules((rs) => [...rs, rule]);
    }
    setVAmt(''); setVUnits(''); setMapCad({ cadence: 'none', days_of_week: '', times_per_day: 1, expected_day: '', unit_rate: '' }); setMapGeo(false);
    return true;
  };

  // Day-replay actions. Confirm a pending standing-event occurrence
  // ("I was there" -> the server applies its units/rands to the expense);
  // delete one or more visits (a confirmed one reverses on the server, a
  // pending one just vanishes). reloadDay keeps the open day in sync.
  const reloadDay = () => {
    if (byDate) api(`/budget/place-visits/?on=${byDate}`).then(setVisits).catch(() => {});
  };
  // Refresh the whole-month visits — the overlay's own counters ("N visits
  // logged here", the event's running spend) read monthVisits, so every log /
  // reverse must refresh it or the card looks like nothing happened.
  const reloadMonth = () => {
    api(`/budget/place-visits/?month=${budget.month}`).then(setMonthVisits).catch(() => {});
  };
  // "Sync locations" — fill this month's map from the previous month: for every
  // expense with NO location yet, copy the place (+ multi-location links) from the
  // same-titled expense in the most recent prior month. Idempotent: skips anything
  // already placed, so a second tap is a no-op.
  const syncPlaces = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await api(`/budget/months/${budget.id}/sync_places/`, { method: 'POST' });
      onChanged();
      const n = r?.linked || 0;
      await ask({ title: 'Sync locations',
        message: n
          ? `Linked ${n} expense${n === 1 ? '' : 's'} to the location${n === 1 ? '' : 's'} they used`
            + (r.prev_month ? ` in ${r.prev_month}.` : ' last month.')
          : 'Every expense that had a match already has its location — nothing to sync.',
        ok: 'Done' });
    } catch {
      await ask({ title: 'Sync locations', message: "Couldn't sync locations right now — try again in a moment.", ok: 'OK' });
    } finally {
      setSyncing(false);
    }
  };
  // Create a NEW expense homed at the place being linked — not just link an
  // existing one — so a pin is never a dead end (mirrors the watches + mobile).
  // spent=false adds a budget line to pay off later; spent=true logs it as paid.
  const createExpenseHere = async (spent) => {
    const amt = Number(String(newHereAmt).replace(/[rR\s,]/g, '')) || 0;
    if (!linking || !newHereTitle.trim() || amt <= 0) return;
    setBusy(true);
    try {
      const body = { budget: budget.id, kind: 'expense', title: newHereTitle.trim().slice(0, 80),
                     amount: amt, source: 'manual', place: linking.id };
      if (spent) { body.actual = amt; body.completed = true; }
      await api('/budget/items/', { method: 'POST', body });
      setNewHereTitle(''); setNewHereAmt('');
      setLinking(null); setLinkItem(null); onChanged();
    } catch {
      setError('Could not add the expense — try again.');
    } finally { setBusy(false); }
  };

  const confirmVisits = async (ids) => {
    for (const id of ids) {
      await api(`/budget/place-visits/${id}/confirm/`, { method: 'POST' }).catch(() => {});
    }
    reloadDay(); onChanged();
  };
  const deleteVisits = async (ids) => {
    for (const id of ids) {
      await api(`/budget/place-visits/${id}/`, { method: 'DELETE' }).catch(() => {});
    }
    setVisits((vs) => vs.filter((x) => !ids.includes(x.id)));
    reloadMonth(); onChanged();
  };
  const deleteRule = async (r) => {
    if (await ask({ title: `Stop “${r.item_title}” @ ${r.place_name}?`,
          message: 'The standing event goes; already-logged days stay and '
            + 'remain deletable one by one.',
          ok: 'Stop it', danger: true, cancel: 'Keep it' })) {
      await api(`/budget/place-visit-rules/${r.id}/`, { method: 'DELETE' }).catch(() => {});
      setRules((rs) => rs.filter((x) => x.id !== r.id));
    }
  };

  // Adjust what's ALREADY DONE on an expense — bump the confirmed formula
  // units by ±1, or set the spent amount outright (separate from pending
  // visits waiting to be confirmed).
  const bumpUnits = async (item, delta, placeId) => {
    const today = new Date().toISOString().slice(0, 10);
    const rate = unitOf(item);
    // ONE model: a unit used IS a logged item (a visit). +1 always logs a unit
    // visit — at the shop when there is one, PLACELESS otherwise — so it shows
    // under Logged and can be deleted anywhere. Its money value is units × rate,
    // so the visit's own amount stays 0 (a flat expense logs its amount instead).
    if (delta > 0) {
      await api('/budget/place-visits/', { method: 'POST', body: {
        place: placeId || null, item: item.id, title: item.title,
        units: rate > 0 ? 1 : 0, amount: rate > 0 ? 0 : (Number(item.amount) || 0),
        on: today, bump: 1, confirmed: true } }).catch(() => {});
      onChanged();
      return;
    }
    // −1 removes this expense's most recent unit log (any place). Legacy paid
    // units that predate visit-logging fall back to nudging the counter down.
    const mine = monthVisits.filter((v) => v.confirmed && Number(v.units) > 0
      && (String(v.item) === String(item.id)
        || (v.title || '').toLowerCase() === (item.title || '').toLowerCase()));
    if (mine.length) {
      const last = mine.reduce((a, b) => (b.id > a.id ? b : a));
      await deleteVisits([last.id]);
      return;
    }
    const nUnits = Math.max(0, (Number(item.reduce_n) || 0) - 1);
    await api(`/budget/items/${item.id}/`,
              { method: 'PATCH', body: { reduce_n: nUnits } }).catch(() => {});
    onChanged();
  };
  const setSpend = async (item, amount) => {
    const v = Number(amount);
    if (!(v >= 0)) return;
    await api(`/budget/items/${item.id}/`,
              { method: 'PATCH', body: { actual: v.toFixed(2) } }).catch(() => {});
    onChanged();
  };

  // AI-suggest a short description for a spend, from its expense + shop +
  // address (on demand — naming a spend is always optional). Best-effort.
  const suggestNote = async (item, place, amount) => {
    try {
      const d = await api('/budget/places/suggest_note/', { method: 'POST',
        body: { title: item?.title || '', shop: place?.name || '',
                address: place?.address || '', amount: String(amount || '') } });
      if (d?.text) setSpendNote(d.text);
    } catch { /* AI is a convenience */ }
  };
  // Set the paid-units count to an EXACT number (the tap-to-type path that sits
  // between the − / + steppers). Whole units only, never negative.
  const setUnitsExact = async (item, count) => {
    // Log the delta through the canonical primitive so an exact-count set is still
    // a deletable, viewable visit (#292) — never a bare reduce_n PATCH.
    const v = Math.max(0, Math.floor(Number(count)));
    const cur = Number(item.reduce_n) || 0;
    if (!(v >= 0) || v === cur) return;
    try {
      if (v > cur) {
        await api(`/budget/items/${item.id}/log/`,
                  { method: 'POST', body: { units: v - cur, place: item.place || null } });
      } else {
        for (let i = 0; i < cur - v; i++) {
          await api(`/budget/items/${item.id}/unlog/`, { method: 'POST', body: { kind: 'unit' } });
        }
      }
    } catch { /* surfaced by the row's own error path */ }
    onChanged();
  };

  // Add ANOTHER visit for the open day — a manual log counts immediately
  // (confirmed), so it moves the budget: units advance the formula's paid
  // counter, money adds to spent. Every visit stays individually deletable.
  const addVisit = async (item, placeId, { units, amount, on, note, rule }) => {
    if (!item) return item;
    const u = Number(units) || 0;
    const rate = unitOf(item);
    const amt = u && rate ? u * rate : Number(amount) || 0;
    if (!amt && !u) { setError('Give the visit units or an amount.'); return item; }
    const day = on || byDate || new Date().toISOString().slice(0, 10);
    try {
      await api('/budget/place-visits/',
                { method: 'POST',
                  body: { place: placeId, item: item.id, title: item.title,
                          amount: amt.toFixed(2), units: u && rate ? u : 0,
                          on: day, note: note || '', rule: rule || null,
                          bump: 1, confirmed: true } });
    } catch (e) {
      setError(e.data?.detail || 'Could not log the visit — try again.');
      return item;
    }
    // The server applied the units/spend ATOMICALLY as it created the visit
    // (bump:1) — no separate PATCH, so two quick logs can't overwrite each
    // other's stale read of actual. `next` mirrors it for batch chaining.
    const next = (u && rate)
      ? { ...item, reduce_n: (Number(item.reduce_n) || 0) + u }
      : { ...item, actual: ((Number(item.actual) || 0) + amt).toFixed(2) };
    reloadDay(); reloadMonth(); onChanged();
    return next;
  };

  // Wipe an expense's spend back to zero — removes EVERY confirmed visit tied
  // to it (each reverses on delete) then forces the counters to nil, so left-
  // over test data or a phantom visit can't keep inflating the shop total.
  const resetSpend = async (item) => {
    if (!item) return;
    if (!await ask({ title: `Reset “${item.title}” spend to R0?`,
          message: 'Removes every logged shop visit for it and clears its '
            + 'spent amount and paid units — a clean slate to log again.',
          ok: 'Reset to R0', danger: true, cancel: 'Keep it' })) return;
    const mine = monthVisits.filter((v) => v.confirmed && v.item === item.id);
    for (const v of mine) {
      await api(`/budget/place-visits/${v.id}/`, { method: 'DELETE' }).catch(() => {});
    }
    await api(`/budget/items/${item.id}/`,
              { method: 'PATCH', body: { reduce_n: 0, actual: null } }).catch(() => {});
    reloadDay(); onChanged();
  };

  // The repeat choice, spelled out — once, every day, or monthly on the
  // chosen date's day. Units for formula expenses, rands for plain ones.
  const repeatChips = (item) => {
    const unit = unitOf(item);
    const u = Number(vUnits) || 0;
    const per = Number(vAmt) || 0;
    const total = u > 0 ? u * (per || unit) : per;
    const what = total
      ? `R${total.toFixed(2).replace(/\.00$/, '')}${u > 0 ? ` (${u}x R${per || unit})` : ''}`
      : 'this spend';
    const DOWN = { '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun' };
    const weekdayNames = (csv) => (csv || '').split(',').filter(Boolean).map((d) => DOWN[d]).filter(Boolean).join(', ');
    const cad = mapGeo ? 'geo' : (mapCad.cadence === 'none' ? 'once' : mapCad.cadence);
    return (
      <div className="mt-1.5" data-guide="map-repeat">
        <CadenceControls value={mapCad} onChange={setMapCad} hideRate />
        <label className="flex items-center gap-2 mt-2.5 text-[11px] cursor-pointer text-slate-500">
          <input type="checkbox" checked={mapGeo} onChange={(e) => setMapGeo(e.target.checked)} />
          <span>Only when I&apos;m there — logged by GPS when you approve, nothing scheduled.</span>
        </label>
        {cad !== 'once' && (
          <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
            {cad === 'geo'
              ? <>Only when you&apos;re there: {what} logs ONLY when your phone detects you at this shop and you approve it — nothing is scheduled.</>
              : <>What this does: {what} is recorded as a PENDING visit{' '}
                  {cad === 'daily'
                    ? `at the end of each day${Number(mapCad.times_per_day) > 1 ? `, ${mapCad.times_per_day}x a day` : ''}`
                    : cad === 'weekly'
                    ? `on ${weekdayNames(mapCad.days_of_week) || 'the chosen days'}`
                    : `once a month on day ${Math.min(Number(mapCad.expected_day) || Number(vDate.slice(8, 10)) || 1, 28)}`}
                  {' '}— WhatsApp asks you to confirm, and only then does its paid counter / spent move. Change or stop it anytime under the daily shop events list.</>}
          </p>
        )}
      </div>
    );
  };

  // 🗓 replay: what was spent WHERE on the chosen day.
  useEffect(() => {
    if (!byDate) { setVisits([]); return; }
    api(`/budget/place-visits/?on=${byDate}`).then(setVisits).catch(() => {});
  }, [byDate]);
  const mq = (x, isPlace = false) => {
    const ql = q.trim().toLowerCase();
    if (!ql) return true;
    if (isPlace) {
      // A place also matches through the EXPENSES living at it — searching
      // "coffee" finds the pin even after the place was renamed.
      return [x.name, x.address].some((s) => (s || '').toLowerCase().includes(ql))
        || expenses.some((i) => i.place === x.id
             && (i.title || '').toLowerCase().includes(ql));
    }
    return [x.title, x.category_name, x.bank_name]
      .some((s) => (s || '').toLowerCase().includes(ql));
  };
  // Transfer notes are money MOVING, not spending — they have no place in
  // the world and never belong on the Spend Map.
  // Guided-tour driver — the Guide dispatches window 'vault-map-tour' events to
  // OPEN the real panels (link, advanced, an expense) using your own first place
  // and expense, so every tour step spotlights a real control instead of empty
  // space. Best-effort and read-only: it opens panels, it never saves.
  useEffect(() => {
    const onTour = (e) => {
      const show = e && e.detail && e.detail.show;
      const place = places[0];
      const exp = (budget.items || []).find((i) => i.kind === 'expense');
      try {
        if (show === 'clear') {
          setLinking(null); setLinkItem(null); setAMode('home');
          setDayExpense(null); setAssigning(null); setPendingPlace(null);
        } else if (show === 'link' && place) {
          setDayExpense(null); setAssigning(null);
          setLinking(place); setLinkItem(null); setAMode('home');
        } else if (show === 'link-advanced' && place && exp) {
          setDayExpense(null); setAssigning(null);
          setLinking(place); setLinkItem(exp); setAMode('visit'); setVRepeat('once');
        } else if (show === 'expense' && place && exp) {
          setLinking(null); setAssigning(null);
          setDayExpense({ place: place.id, item: exp.id, title: exp.title,
                          place_name: place.name });
          setDeTab('visits');
        }
      } catch { /* never break the map for a tour */ }
    };
    window.addEventListener('vault-map-tour', onTour);
    return () => window.removeEventListener('vault-map-tour', onTour);
  }, [places, budget]);

  const expenses = (budget.items || []).filter((i) => i.kind === 'expense'
                                              && i.source !== 'transfer');
  // Bank scope predicates — the ONE gate every place total passes through, so a
  // chip narrows the pins, the place list and the stat tiles from the same
  // per-place money functions (no second calc). A visit belongs to a bank via
  // its own bank field, else the bank of the expense it logged against.
  const onBank = (i) => !bankFilter || String(i.bank) === String(bankFilter);
  const visitOnBank = (v) => {
    if (!bankFilter) return true;
    if (v.bank != null && v.bank !== '') return String(v.bank) === String(bankFilter);
    const vi = (budget.items || []).find((x) => x.id === v.item);
    return vi ? String(vi.bank) === String(bankFilter) : false;
  };
  // Bank chips — the user's actual accounts (dashboard parity); fall back to the
  // banks that appear on this month's mapped expenses when the list isn't passed.
  const bankChips = (banks.length
    ? banks.filter((b) => !b.archived)
    : Array.from(new Map((budget.items || [])
        .filter((i) => i.bank).map((i) => [i.bank, { id: i.bank, name: i.bank_name || 'Bank' }]))
        .values()))
    .map((b) => [b.id, b.name]);
  // "Not on the map yet" means NOT MAPPED AT ALL: a home place, an EXTRA
  // location, OR a standing shop event all count as assigned — any of them
  // removes it from this list.
  const eventTitles = new Set(rules.map((r) => (r.item_title || '').toLowerCase()));
  const unplaced = expenses.filter((i) => !i.place
    && !(i.places && i.places.length)
    && !eventTitles.has((i.title || '').toLowerCase()));
  // PLACED expenses matching the search get their own list — an expense on
  // the map must stay findable by its own name, not just its pin's.
  // (AFTER `expenses` exists — evaluating before it blanked the screen.)
  const placedMatches = q.trim()
    ? expenses.filter((i) => i.place && mq(i)) : [];

  // spend per place this month
  const spendByPlace = {};
  for (const i of expenses) {
    if (i.place && onBank(i)) {
      spendByPlace[i.place] = (spendByPlace[i.place] || 0) +
        Number(i.actual ?? i.amount);
    }
  }
  // Real spend AT a place this month: expenses HOMED there (their usage) plus
  // confirmed visits of expenses only VISITED there (not homed, so not already
  // counted). A shop like Simpe bru — visited but not homed — now shows its
  // true spend and units.
  // Confirmed visits at a place this month — a R 0,00 "just visited" still
  // counts. Shown on every place row and pin (Pass 4, one ledger).
  const placeVisits = (pid) => monthVisits.filter((v) => v.place === pid && v.confirmed && visitOnBank(v)).length;
  const placeUsage = (pid) => {
    let spent = 0, units = 0;
    for (const i of expenses) {
      if (i.place === pid && onBank(i)) {
        let s = Number(i.actual || 0) + (Number(i.reduce_n) || 0) * unitOf(i);
        // A DONE expense with no logged actual (marked paid, e.g. Irish Eisbein
        // R570) is fully spent — count its amount, not R0.
        if (s <= 0 && i.completed) s = Number(i.amount || 0);
        // Fold in this expense's confirmed visits here (an imported/placed log
        // may not be in `actual` yet): take the LARGER — never double-counts.
        let vSum = 0;
        for (const v of monthVisits) {
          if (v.item === i.id && v.place === pid && v.confirmed && visitOnBank(v)) {
            vSum += Number(v.units || 0) > 0
              ? Number(v.units) * unitOf(i)
              : Number(v.amount || 0);
          }
        }
        spent += Math.max(s, vSum);
        units += Number(i.reduce_n) || 0;
      }
    }
    for (const v of monthVisits) {
      if (v.place !== pid || !v.confirmed || !visitOnBank(v)) continue;
      const vi = (budget.items || []).find((x) => x.id === v.item);
      if (vi && vi.place === pid) continue;   // homed → already counted above
      const vu = Number(v.units || 0);
      // A unit visit's money is units × the expense's rate (it carries amount 0);
      // a money visit's money is its amount.
      spent += vu > 0 ? vu * (vi ? unitOf(vi) : 0) : Number(v.amount || 0);
      units += vu;
    }
    return { spent, units };
  };
  // Money still COMING (upcoming) at a place — homed/linked, not-done expenses'
  // still-to-pay (budget − spent). Powers the "Upcoming" lens on the top bar.
  const comingByPlace = (pid) => {
    let coming = 0;
    for (const i of expenses) {
      const linked = i.place === pid || (i.places || []).includes(pid);
      if (!linked || i.completed || !onBank(i)) continue;
      const spent = Number(i.actual || 0) + (Number(i.reduce_n) || 0) * unitOf(i);
      coming += Math.max(0, Number(i.amount || 0) - spent);
    }
    return coming;
  };
  // A shop earns a spot in the right-bar list (and a pin) only while something
  // still ties an expense to it: a linked expense (home OR extra location), a
  // standing event, spend, or a logged visit this month. Delete the last
  // expense and the orphaned shop drops off — however it was deleted.
  const placeHasActivity = (p) => (
    expenses.some((x) => (x.place === p.id || (x.places || []).includes(p.id)) && onBank(x))
    // A standing event counts as activity only in the unscoped view — a bank
    // chip narrows to logged money, so an event-only place drops out cleanly.
    || (!bankFilter && rules.some((r) => r.place === p.id))
    || (spendByPlace[p.id] || 0) > 0
    || monthVisits.some((v) => v.place === p.id && visitOnBank(v))
  );

  useEffect(() => { api('/budget/places/').then(setPlaces).catch(() => {}); }, []);
  // The Spend-Map filter — lens, pins (the "linked only" toggle), range and
  // query — is saved PER USER (BudgetSettings.map_*), so web, phone and watch
  // all open the map on the same view. Load it once, then persist changes.
  const mapPrefsLoaded = useRef(false);
  useEffect(() => {
    api('/budget/settings/').then((d) => {
      setMapPins(d.map_pins || 'all');
      setLens(d.map_lens || 'spent');
      const r = d.map_range || 'month';
      setByDate(/^\d{4}-\d{2}-\d{2}$/.test(r) ? r : '');
      if (d.map_query) setQ(d.map_query);
    }).catch(() => {}).finally(() => { mapPrefsLoaded.current = true; });
  }, []);
  const setPinPref = async (v) => {
    setMapPins(v);
    await api('/budget/settings/', { method: 'PATCH', body: { map_pins: v } }).catch(() => {});
  };
  // Persist lens + range whenever they change (after the initial load), so the
  // next surface opens on the same lens and day.
  useEffect(() => {
    if (!mapPrefsLoaded.current) return;
    api('/budget/settings/', { method: 'PATCH',
      body: { map_lens: lens, map_range: byDate || 'month' } }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, byDate]);
  // Persist the search text, debounced so a PATCH never fires per keystroke.
  useEffect(() => {
    if (!mapPrefsLoaded.current) return;
    const t = setTimeout(() => {
      api('/budget/settings/', { method: 'PATCH', body: { map_query: q } }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => {
    api(`/budget/place-visits/?month=${budget.month}`).then(setMonthVisits).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget.month, budget.items]);
  useEffect(() => { api('/budget/place-visit-rules/').then(setRules).catch(() => {}); }, []);

  // Build the map once — centred on WHERE YOU ARE (browser geolocation),
  // falling back to Johannesburg if permission is declined.
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: false }).setView(JHB, 11);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // ALWAYS default to the user's current location. Even if the fix arrives
    // after the first pin redraw, this re-centres on the user and marks the
    // view as fix-owned so the redraw's fitBounds can never steal it back.
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        posRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        hasFixRef.current = true;
        map.setView([pos.coords.latitude, pos.coords.longitude], 14);
        L.circleMarker([pos.coords.latitude, pos.coords.longitude],
          { radius: 7, color: '#38BDF5', fillColor: '#38BDF5', fillOpacity: 0.9 })
          .addTo(map).bindPopup('You are here');
      },
      () => {},   // declined → JHB default stands, pins frame themselves once
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Area-in-view legend (Design spec): reverse-geocode the map centre to a suburb
  // as you pan. Debounced, best-effort — the chip just hides if it can't resolve.
  useEffect(() => {
    const map = mapRef.current; if (!map) return undefined;
    let t;
    const upd = () => {
      clearTimeout(t);
      t = setTimeout(async () => {
        const c = map.getCenter();
        try {
          const d = await api('/budget/places/reverse_geocode/',
                              { method: 'POST', body: { lat: c.lat, lng: c.lng } });
          setAreaLabel(d.suburb || d.name || '');
        } catch { /* legend is best-effort */ }
      }, 700);
    };
    map.on('moveend', upd);
    upd();
    return () => { map.off('moveend', upd); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #306 — completed trips: load them so their saved GPS route can be replayed
  // on the map with distance + cost; business trips carry the SARS badge.
  const loadTrips = () => api('/budget/trips/')
    .then((rows) => setTrips((Array.isArray(rows) ? rows : [])
      .filter((t) => t.status === 'done' || (t.route || []).length > 1)))
    .catch(() => {});
  useEffect(() => { loadTrips(); /* eslint-disable-next-line */ }, []);

  // Draw ONLY the selected trip. The map shows just the start (green) → end
  // (amber) markers by default — the movement — and the full road it drove is
  // drawn on top only once the owner asks (routeShown), per the Spend Map spec:
  // "the route stays hidden until you tap the ride, then it draws where it went".
  useEffect(() => {
    const map = mapRef.current; if (!map) return undefined;
    if (tripRoutes.current) { map.removeLayer(tripRoutes.current); tripRoutes.current = null; }
    if (!selTrip) return undefined;
    const pts = (selTrip.route || []).filter((pt) => Array.isArray(pt) && pt.length === 2);
    const grp = L.layerGroup();
    const a = pts[0] || (selTrip.start_lat != null ? [Number(selTrip.start_lat), Number(selTrip.start_lng)] : null);
    const b = pts[pts.length - 1] || (selTrip.end_lat != null ? [Number(selTrip.end_lat), Number(selTrip.end_lng)] : null);
    if (a) L.circleMarker(a, { radius: 6, color: '#34d399', fillColor: '#34d399', fillOpacity: 0.9 }).addTo(grp);
    if (b && (!a || a[0] !== b[0] || a[1] !== b[1]))
      L.circleMarker(b, { radius: 6, color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.9 }).addTo(grp);
    if (routeShown && pts.length >= 2)
      L.polyline(pts, { color: '#22c55e', weight: 5, opacity: 0.95 }).addTo(grp);
    grp.addTo(map); tripRoutes.current = grp;
    return undefined;
  }, [selTrip, routeShown]);

  // Fit the map to the selected trip's road.
  useEffect(() => {
    const map = mapRef.current; if (!map || !selTrip) return;
    const pts = (selTrip.route || []).filter((pt) => Array.isArray(pt) && pt.length === 2);
    if (pts.length) { try { map.fitBounds(pts, { padding: [40, 40] }); } catch { /* single point */ } }
  }, [selTrip]);

  // Jump the map back to the user's live position — handy after hovering a far
  // result has panned the view away.
  const recenterOnMe = () => {
    const map = mapRef.current; if (!map) return;
    const go = (lat, lng) => map.setView([lat, lng], 14);
    if (posRef.current) go(posRef.current.lat, posRef.current.lng);
    navigator.geolocation?.getCurrentPosition(
      (pos) => { posRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                 go(pos.coords.latitude, pos.coords.longitude); },
      () => {}, { timeout: 5000 });
  };

  // ONE-TAP place from where you stand — pin your current spot as a saved place
  // (named "Home" to start, rename it right away). No searching your own address.
  // 🚗 TRIP TRACKER — start records your GPS route (green line) and streams
  // points to the backend; end asks for a cost that can settle a transport
  // expense. Foreground tracking (keep the tab open during the ride).
  const startTrip = async () => {
    if (trip) return;
    if (!navigator.geolocation) { setError('Location is needed to track a trip.'); return; }
    const label = await ask({ title: 'Start a trip',
      message: 'Name it — e.g. "Uber to Dad".', input: true, value: 'Uber', ok: 'Start' });
    if (label === null) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const lat = pos.coords.latitude, lng = pos.coords.longitude;
      try {
        const t = await api('/budget/trips/', { method: 'POST',
          body: { label: (label || 'Trip').slice(0, 120), start_lat: lat, start_lng: lng,
                  started_at: new Date().toISOString(), route: [[lat, lng]], status: 'active' } });
        setTrip(t);
        tripPts.current = [[lat, lng]];
        const map = mapRef.current;
        if (map) {
          if (tripLine.current) map.removeLayer(tripLine.current);
          tripLine.current = L.polyline([[lat, lng]],
            { color: '#34d399', weight: 4, opacity: 0.9 }).addTo(map);
          map.setView([lat, lng], 15);
        }
        tripWatch.current = navigator.geolocation.watchPosition((p) => {
          const la = p.coords.latitude, ln = p.coords.longitude;
          const pts = tripPts.current;
          const last = pts[pts.length - 1];
          if (last && Math.abs(last[0] - la) < 0.00005 && Math.abs(last[1] - ln) < 0.00005) return;
          pts.push([la, ln]);
          if (tripLine.current) tripLine.current.setLatLngs(pts);
          if (mapRef.current) mapRef.current.panTo([la, ln]);
          api(`/budget/trips/${t.id}/point/`, { method: 'POST', body: { lat: la, lng: ln } }).catch(() => {});
        }, () => {}, { enableHighAccuracy: true, maximumAge: 4000, timeout: 15000 });
      } catch (e) { setError(e.data?.detail || e.message); }
    }, () => setError('Could not get your location for the trip.'),
       { enableHighAccuracy: true });
  };
  // Stop tracking → open the saved-trip card. The live GPS watch stops now; the
  // trip is finalized (and any fare settled) when the owner confirms on the card,
  // so they can first decide whether it was a paid ride (taxi / e-hailing).
  const endTrip = async () => {
    if (!trip) return;
    if (tripWatch.current != null) { navigator.geolocation.clearWatch(tripWatch.current); tripWatch.current = null; }
    const pts = tripPts.current;
    const last = pts[pts.length - 1] || [];
    // Pull the server's accumulated distance/route so the card shows real numbers.
    let fresh = trip;
    try { fresh = await api(`/budget/trips/${trip.id}/`); } catch { /* keep the local copy */ }
    if (tripLine.current && mapRef.current) { mapRef.current.removeLayer(tripLine.current); tripLine.current = null; }
    setSavedTrip({ ...fresh, _endLat: last[0] ?? fresh.end_lat ?? null, _endLng: last[1] ?? fresh.end_lng ?? null });
    setRideMode(false); setRideFare(''); setRideItem(''); setRouteShown(false);
    setSelTrip({ ...fresh });   // start → end markers show on the map straight away
    setTrip(null);
  };

  // The transport expense a fare most likely settles onto (taxi / e-hailing) —
  // the default pick in the "Which expense" chooser on the Paid-ride card.
  const rideGuess = () => (expenses.find((e) =>
    /taxi|uber|bolt|e-hail|e hail|\bride\b|lift|\bcab\b|transport|travel|gautrain|petrol|fuel/i.test(e.title || ''))
    || expenses[0] || {}).id || '';

  // Confirm the saved trip: end it server-side. A paid ride sends its fare + the
  // chosen expense so the backend logs a real Transport spend; a plain trip just
  // finalizes for the travel logbook (no expense).
  const confirmTrip = async (asRide) => {
    if (!savedTrip) return;
    setRideBusy(true);
    const body = { end_lat: savedTrip._endLat, end_lng: savedTrip._endLng };
    if (asRide) {
      body.is_ride = true;
      body.cost = (rideFare && Number(rideFare) > 0) ? Number(rideFare) : null;
      if (rideItem) body.item = rideItem;
    }
    try {
      await api(`/budget/trips/${savedTrip.id}/end/`, { method: 'POST', body });
    } catch (e) { setError(e.data?.detail || e.message); }
    setRideBusy(false); setSavedTrip(null); setSelTrip(null); setRouteShown(false);
    onChanged(); loadTrips();
  };

  const pinMyLocation = async () => {
    setError('');
    const fix = await new Promise((res) => {
      if (posRef.current) return res(posRef.current);
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => { posRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                   res(posRef.current); },
        () => res(null), { timeout: 6000, maximumAge: 60000 });
    });
    if (!fix) { setError('Turn on location to pin where you are.'); return; }
    try {
      const place = await api('/budget/places/', { method: 'POST',
        body: { name: 'Home', address: '', lat: fix.lat, lng: fix.lng, kind: 'home' } });
      setPlaces((ps) => [...ps, place]);
      mapRef.current?.setView([fix.lat, fix.lng], 15);
      setEditPlace(place);   // open the editor so you can name it + add the address
    } catch (e) {
      setError(e.data?.detail || 'Could not pin your location.');
    }
  };

  // Hover a search result → show WHERE it is: pan there and flag it in amber.
  // Leaving the row clears the flag. Purely visual; picking is still a click.
  // Expenses shown for a selected place: homed here, linked via a standing
  // event, or having logged a visit here. Same set the inline breakdown used.
  const expensesHere = (place) => {
    if (!place) return [];
    const by = new Map();
    for (const x of expenses)
      if (x.place === place.id || (x.places || []).includes(place.id)) by.set(x.id, x);
    const titles = new Set(rules.filter((r) => r.place === place.id)
      .map((r) => (r.item_title || '').toLowerCase()).filter(Boolean));
    if (titles.size) for (const x of expenses)
      if (titles.has((x.title || '').toLowerCase())) by.set(x.id, x);
    for (const v of monthVisits)
      if (v.place === place.id && v.confirmed && v.item) {
        const x = expenses.find((e) => e.id === v.item);
        if (x) by.set(x.id, x);
      }
    return [...by.values()];
  };

  const previewOnMap = (c) => {
    const map = mapRef.current; if (!map || c?.lat == null) return;
    // Zoom IN to show it clearly (keep the zoom if already close), not just pan.
    map.setView([Number(c.lat), Number(c.lng)], Math.max(map.getZoom() || 13, 15),
                { animate: true });
    if (hoverRef.current) map.removeLayer(hoverRef.current);
    hoverRef.current = L.circleMarker([Number(c.lat), Number(c.lng)],
      { radius: 11, color: '#F59E0B', weight: 3,
        fillColor: '#F59E0B', fillOpacity: 0.3 }).addTo(map);
  };
  const clearPreview = () => {
    if (hoverRef.current && mapRef.current) {
      mapRef.current.removeLayer(hoverRef.current); hoverRef.current = null;
    }
  };

  // Center the map on the linked place whenever a shop overlay (editPlace) or an
  // event editor (editRule) opens — so opening an event or a pinned expense
  // focuses the map on its location. Works on TOUCH too (hover-preview never
  // fires there). Only zooms IN; keeps the current zoom if already close.
  useEffect(() => {
    const map = mapRef.current;
    const pid = editPlace?.id ?? editRule?.place ?? null;
    if (!map || pid == null) return;
    const p = places.find((x) => x.id === pid);
    if (p && p.lat != null && p.lng != null) {
      map.setView([Number(p.lat), Number(p.lng)],
                  Math.max(map.getZoom() || 13, 15), { animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPlace, editRule]);

  // Deep-link focus: when the map opens ON an expense (openItem \u2192 assigning),
  // pan + zoom to that expense's home place so the map lands where the money
  // goes — not the default country view. Retries briefly because the Leaflet
  // map and the places list both mount asynchronously as the overlay opens.
  useEffect(() => {
    if (!assigning) return undefined;
    const pid = (assignCtx && assignCtx.id) != null ? assignCtx.id : (assigning.place != null ? assigning.place : null);
    if (pid == null) return undefined;
    let tries = 0; let timer = null;
    const focus = () => {
      const map = mapRef.current;
      const pl = places.find((x) => String(x.id) === String(pid));
      if (map && pl && pl.lat != null && pl.lng != null) {
        map.setView([Number(pl.lat), Number(pl.lng)], Math.max(map.getZoom() || 13, 15), { animate: true });
        return;
      }
      if (tries++ < 12) timer = setTimeout(focus, 250);
    };
    focus();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigning && assigning.id, assignCtx && assignCtx.id, places.length]);

  // Search any store or address — geocoded exactly as typed.
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const runSearch = async () => {
    if (!search.trim()) return;
    setBusy(true); setError('');
    // Bias to WHERE YOU ARE: if we have no fix yet, grab one before searching so
    // "gas" finds the station down your road, not a same-named one overseas.
    if (!posRef.current && navigator.geolocation) {
      await new Promise((res) => navigator.geolocation.getCurrentPosition(
        (pos) => { posRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; res(); },
        () => res(), { timeout: 4000, maximumAge: 60000 }));
    }
    try {
      // Your typed words win — no AI "enhance" round-trip (that was the slow
      // part and the source of overseas drift). Just the query + your location.
      const d = await api('/budget/places/resolve/',
                          { method: 'POST',
                            body: { query: search.trim(),
                                    ...(posRef.current || {}) } });
      setSearchResults(d);
      if (d.candidates[0]) {
        mapRef.current?.setView([d.candidates[0].lat, d.candidates[0].lng], 15);
      }
    } catch (e) {
      setError(e.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  const pickSearchResult = async (c) => {
    // Assigning: the result becomes the expense's place (home or visit).
    // Moving: the chosen place slides to the new address. Otherwise the
    // result is saved and the NEXT question opens: which expense fits here?
    const cand = { name: (c.name || search.trim()).slice(0, 60),
                   address: c.address, lat: c.lat, lng: c.lng };
    if (assigning) {
      // NEVER instant: the pick waits for the 📝/🎯 confirm below.
      setPendingPlace(cand);
    } else if (moving) {
      const up = await api(`/budget/places/${moving.id}/`,
                           { method: 'PATCH',
                             body: { address: c.address, lat: c.lat, lng: c.lng } })
        .catch(() => null);
      if (up) {
        setPlaces((ps) => ps.map((x) => (x.id === moving.id ? up : x)));
        mapRef.current?.setView([Number(up.lat), Number(up.lng)], 15);
      }
      setMoving(null);
    } else {
      let place = places.find((p) => p.name.toLowerCase() === cand.name.toLowerCase());
      if (!place) {
        place = await api('/budget/places/', { method: 'POST', body: cand })
          .catch(() => null);
        if (place) setPlaces((ps) => [...ps, place]);
      }
      if (place) { setLinking(place); setLinkItem(null); setAMode('home'); }
    }
    setSearch(''); setSearchResults(null);
  };

  // ALWAYS searching while you type — locations, addresses, shops — after
  // three characters, debounced so the geocoder breathes.
  useEffect(() => {
    if (search.trim().length < 3) { setSearchResults(null); return undefined; }
    const t = setTimeout(runSearch, 550);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Redraw pins whenever places/spend change. With a 🗓 date chosen the MAP
  // itself replays that day — pins size to the day's visits; clear the date
  // and the whole month returns.
  const dayByPlace = {};
  for (const v of visits) {
    if (!visitOnBank(v)) continue;   // day replay honours the active bank chip too
    const vu = Number(v.units || 0);
    const vi = vu > 0 ? (budget.items || []).find((x) => x.id === v.item) : null;
    dayByPlace[v.place] = (dayByPlace[v.place] || 0)
      + (vu > 0 ? vu * (vi ? unitOf(vi) : 0) : Number(v.amount || 0));
  }
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    markersRef.current.clear();
    const pts = [];
    const coming = !byDate && lens === 'coming';   // the "Upcoming" lens
    // Sanity bounds for ORPHAN pins (a place with no linked expense/visit/event):
    // only pin an orphan that sits near your REAL (active) places. A mis-geocoded
    // orphan — a name that resolved to the wrong country — otherwise floats on the
    // map with no matching list row (the "location not in the list" bug).
    const _actPts = places.filter((pl) => pl.lat != null && pl.lng != null && placeHasActivity(pl))
      .map((pl) => [Number(pl.lat), Number(pl.lng)]);
    const _bbox = _actPts.length ? _actPts.reduce((b, [la, ln]) => ({
      minLa: Math.min(b.minLa, la), maxLa: Math.max(b.maxLa, la),
      minLn: Math.min(b.minLn, ln), maxLn: Math.max(b.maxLn, ln),
    }), { minLa: 90, maxLa: -90, minLn: 180, maxLn: -180 }) : null;
    const PAD = 3;   // ~330 km of slack so genuinely nearby orphans still pin
    const farOrphan = (p) => !!_bbox && (Number(p.lat) < _bbox.minLa - PAD || Number(p.lat) > _bbox.maxLa + PAD
      || Number(p.lng) < _bbox.minLn - PAD || Number(p.lng) > _bbox.maxLn + PAD);
    for (const p of places) {
      // Month view sizes the pin by TOTAL spend at the place — homed expenses
      // AND visits. Upcoming lens sizes by what's still to come there.
      const spend = (byDate ? dayByPlace[p.id]
                            : coming ? comingByPlace(p.id)
                                     : placeUsage(p.id).spent) || 0;
      if (coming && !spend) continue;   // Upcoming: only shops that still owe
      if (byDate && !spend) {
        // Day view still pins shops with a standing event DUE that day (the
        // "potential spend" ones) — otherwise today's shops show in the list
        // with no marker on the map.
        const dayNum = Number(byDate.slice(8, 10)) || 1;
        const dueToday = rules.some((r) => String(r.place) === String(p.id) && r.active
          && !((budget.items || []).find((x) => x.kind === 'expense'
                && (x.title || '').toLowerCase() === (r.item_title || '').toLowerCase())?.completed)
          && (r.cadence === 'daily' || r.cadence === 'geo'
              || (r.cadence === 'monthly' && dayNum === (r.day_of_month || 1))));
        if (!dueToday) continue;
      }
      // An "orphan" place has no linked expense/event/visit. Under the 'linked'
      // preference we hide it; under 'all' (default) it still pins — muted and
      // smaller — so you can tap it to add/link an expense right there.
      const orphan = !byDate && !coming && !placeHasActivity(p);
      if (orphan && mapPins === 'linked') continue;
      // A bank chip scopes to that account's money — a place with no activity on
      // it is out of scope entirely, not a muted orphan pin.
      if (orphan && bankFilter) continue;
      if (orphan && farOrphan(p)) continue;   // hide mis-geocoded orphan pins far from your real places
      // Bubble sized by AREA, not radius: diameter = clamp(14, sqrt(rands)/k, 64)
      // so a R3 000 place carries ~10x the ink of a R300 one (sqrt gives
      // area proportional to rands; an additive base would flatten it). k=1
      // is tuned for web; halve the diameter for Leaflet's pixel radius.
      const K = 1;
      const diameter = Math.max(14, Math.min(64, Math.sqrt(Math.max(0, spend)) / K));
      const radius = orphan ? 7 : diameter / 2;
      const marker = L.circleMarker([Number(p.lat), Number(p.lng)], {
        radius,
        color: orphan ? '#64748b' : (coming ? '#3DB8F5' : '#8B5CF6'),
        fillColor: orphan ? '#334155' : (coming ? '#3DB8F5' : '#6366F1'),
        fillOpacity: orphan ? 0.35 : 0.55, weight: 2,
      }).addTo(layer);
      const lines = byDate
        ? visits.filter((v) => v.place === p.id)
            .map((v) => `${v.title || 'spend'} — ${fmtR(v.amount)}`
              + (v.confirmed ? '' : ' (to confirm)')).join('<br/>')
        : (() => {
            const rows = [];
            const homedIds = new Set();
            const today = new Date().toISOString().slice(0, 10);
            // The event (if any) for an expense at THIS shop, spelled out:
            // its per-day (or per-month) value and how many are still left
            // for the day — so the bubble reads as the plan, not just a total.
            const eventNote = (item, title) => {
              const r = rules.find((x) => x.place === p.id
                && (x.active !== false)
                && (x.item_title || '').toLowerCase() === (title || '').toLowerCase());
              if (!r) return '';
              // A settled expense's event is DONE for the month — the night run
              // skips it (bi.completed), so the map says so too instead of "due".
              if (item && item.completed) return '<br/>&nbsp;&nbsp;✓ done — settled this month';
              const rate = item ? unitOf(item) : 0;
              const evU = Number(r.units) || 0;
              const evA = Number(r.amount) || 0;
              const perR = evU ? evU * rate : evA;         // one visit, in rands
              const perLbl = evU ? `${evU}× @ ${fmtR(rate)}` : fmtR(evA);
              if (r.cadence === 'monthly') {
                const day = Number(r.day_of_month) || 1;
                const doneM = monthVisits.some((v) => v.place === p.id && v.confirmed
                  && (item ? v.item === item.id
                    : (v.title || '').toLowerCase() === (title || '').toLowerCase()));
                return `<br/>&nbsp;&nbsp;plan: ${perLbl} on day ${day} · `
                  + (doneM ? 'done this month' : 'due this month');
              }
              if (r.cadence === 'daily') {
                const times = Math.max(1, Number(r.times_per_day) || 1);
                const doneToday = monthVisits.filter((v) => v.place === p.id
                  && v.confirmed && v.on === today && (item ? v.item === item.id
                    : (v.title || '').toLowerCase() === (title || '').toLowerCase())).length;
                const leftToday = Math.max(0, times - doneToday);
                return `<br/>&nbsp;&nbsp;plan: ${times}× ${perLbl}/day = ${fmtR(perR * times)}`
                  + ` · ${leftToday} of ${times} left today`;
              }
              return `<br/>&nbsp;&nbsp;plan: ${perLbl} when you're here`;   // geo
            };
            // Expenses HOMED here: full formula total, spent, units, leftover.
            for (const i of expenses.filter((x) => x.place === p.id)) {
              homedIds.add(i.id);
              const rate = unitOf(i);
              const done = Number(i.reduce_n) || 0;
              const spent = Number(i.actual || 0) + done * rate;
              const total = Number(i.amount || 0);
              const left = total - spent;
              const cm = String(i.calculation || '').replace(/\s+/g, '')
                .match(/^([0-9.]+)\*([0-9]+)/);
              const count = cm ? Number(cm[2]) : 0;
              const up = rate > 0 && count ? ` (${done}/${count} units)` : '';
              rows.push(`${i.title}: ${fmtR(spent)} spent${up} · `
                + `${fmtR(left)} of ${fmtR(total)} left` + eventNote(i, i.title));
            }
            // Expenses only VISITED here (not homed): their spend + units.
            const byItem = {};
            for (const v of monthVisits) {
              if (v.place !== p.id || !v.confirmed) continue;
              if (v.item && homedIds.has(v.item)) continue;
              const k = v.item || 't:' + (v.title || '');
              const e = byItem[k] = byItem[k]
                || { title: v.title, itemId: v.item, spent: 0, units: 0 };
              e.spent += Number(v.amount || 0);
              e.units += Number(v.units || 0);
            }
            for (const e of Object.values(byItem)) {
              const vitem = (budget.items || []).find((x) => x.id === e.itemId);
              rows.push(`${e.title || 'spend'}: ${fmtR(e.spent)} spent`
                + (e.units ? ` (${e.units}× units)` : '')
                + eventNote(vitem, e.title));
            }
            return rows.join('<br/>');
          })();
      const shownSpend = byDate ? spend : placeUsage(p.id).spent;
      const vCount = monthVisits.filter((v) => v.place === p.id && v.confirmed).length;  // #279 — visits at this shop
      // Design final spec: PIN CLICK = PANEL. No on-map popup — a pin and its list
      // row do the identical thing: open the shared PlaceSheet and focus the map.
      marker.on('click', () => { acc.open('place:' + p.id); previewOnMap(p); });
      const _plabel = `${p.name} · ${fmtR(shownSpend)}${vCount > 0 ? ` · ${vCount}× visit${vCount === 1 ? '' : 's'}` : ''}`;
      marker.bindTooltip(_plabel, { direction: 'top', offset: [0, -4], opacity: 0.95, permanent: p.id === openList });
      markersRef.current.set(p.id, { marker, label: _plabel });
      pts.push([Number(p.lat), Number(p.lng)]);
    }
    // Frame the pins ONLY as the no-location-fix fallback, and ONLY once — a
    // geolocation fix owns the initial centre (the map opens on WHERE YOU ARE),
    // and after the first draw the user's own pan/zoom is never overridden.
    if (pts.length && !hasFixRef.current && !didAutoFitRef.current) {
      mapRef.current?.fitBounds(pts, { padding: [40, 40], maxZoom: 13 });
      didAutoFitRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, budget.items, byDate, visits, monthVisits, rules, lens, mapPins, bankFilter]);

  // Pin labels (Design spec): the SELECTED pin shows its "Name · R amount" label
  // always; every other pin shows it only on hover. Rebinds tooltips on selection
  // change without redrawing the markers (so the map never re-fits).
  useEffect(() => {
    markersRef.current.forEach(({ marker, label }, id) => {
      marker.unbindTooltip();
      marker.bindTooltip(label, { direction: 'top', offset: [0, -4], opacity: 0.95, permanent: id === openList });
    });
  }, [openList]);

  // Lock the page behind the full-screen map overlay so ONLY the rail scrolls.
  // Without this the body keeps its own scrollbar, which sits right beside the
  // rail's — reading as a double scrollbar on the This-month-by-place list.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Focus-scroll: opening a place (from the list OR a pin) smooth-scrolls its
  // row into view inside the list (Spend Map mockup behaviour).
  useEffect(() => {
    if (!openList) return undefined;
    const el = document.getElementById(`web-place-${openList}`);
    // The rail is the one scroll region now (no inner list scrollbar), so scroll
    // the opened row into view through its nearest scroll ancestor.
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return undefined;
  }, [openList]);

  // Manual pin drop while assigning.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onClick = (e) => {
      if (dropMode && assigning) {
        // Dropped pins confirm like every other pick — Basic/Advanced next.
        setPendingPlace({ name: assigning.title.slice(0, 60), address: '',
                          lat: e.latlng.lat, lng: e.latlng.lng });
        setDropMode(false);
      }
    };
    map.on('click', onClick);
    return () => map.off('click', onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropMode, assigning, alsoRecurring]);

  const startAssign = async (item, ctx = null) => {
    // ONE flow at a time: assigning an expense cancels a pin-move or a
    // fresh-place link in progress — no more crossed wires.
    setMoving(null); setLinking(null); setLinkItem(null); setPendingPlace(null);
    setAssigning(item); setAssignCtx(ctx); setCandidates(null); setError(''); setDropMode(false);
    setOTab('basic');   // always open on Basic — log the spend first
    // Homed here → Basic (its home). Opened under a shop it only visits →
    // Advanced, ready to log a spend/units right there.
    setAMode(ctx && String(item.place) !== String(ctx.id) ? 'visit' : 'home');
    setSearch(''); setSearchResults(null);
    // NO auto-search: the overlay opens on the expense's own controls and its
    // already-linked shops. A shop search runs ONLY when you use the search box.
  };

  // Deep-link from the dashboard: open a given expense straight on its Logged
  // tab so "see what it was spent on" lands where the notes live.
  useEffect(() => {
    if (openItem) { startAssign(openItem); setOTab('logged'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItem && openItem.id]);

  // Unhome an expense with a CHOICE: keep the shop/address linked as an EXTRA
  // location (still on the map, just not the home), or unlink it completely.
  const unhomeExpense = async (item, keepAsExtra) => {
    const cur = item.place;
    if (!cur) return;
    const extra = (item.places || []).filter((x) => x !== cur);
    const body = keepAsExtra
      ? { place: null, places: [...extra, cur] }
      : { place: null, places: extra };
    if (!keepAsExtra) setAssignCtx(null);
    await api(`/budget/items/${item.id}/`, { method: 'PATCH', body }).catch(() => {});
    onChanged();
  };
  const askUnhome = async (item) => {
    if (!item?.place) return;
    const hp = places.find((x) => x.id === item.place);
    const choice = await ask({
      title: hp ? hp.name : 'Home shop',
      message: 'Keep it linked to this expense as an extra location, or unlink it '
        + 'from the expense completely?',
      ok: 'Keep as extra location', alt: 'Unlink completely', cancel: 'Cancel',
    });
    if (choice === true) return unhomeExpense(item, true);
    if (choice === 'alt') return unhomeExpense(item, false);
    return undefined;
  };

  const linkCandidate = async (cand, asHome) => {
    // Link a chosen shop (a saved place OR a fresh search candidate) to the
    // OPEN expense — as its HOME (master) or as ANOTHER spend location. Both
    // keep the existing links: a new home just demotes the old home to an extra.
    setError('');
    try {
      let pl = places.find((x) =>
        (x.name || '').toLowerCase() === (cand.name || '').toLowerCase());
      if (!pl) {
        pl = await api('/budget/places/', { method: 'POST',
          body: { name: cand.name, address: cand.address || '',
                  lat: cand.lat, lng: cand.lng,
                  kind: cand.source === 'address' ? 'address' : 'shop' } });
        setPlaces((ps) => [...ps, pl]);
      }
      const live = (budget.items || []).find((x) => x.id === assigning.id) || assigning;
      // A place linked ONLY as an extra location (item.places) never surfaced on
      // the expense row: the row/DetailCard read the HOME fk (item.place), whose
      // name is blank when only the M2M is set — so the user saw "No place linked"
      // and reported the link "didn't take". The FIRST place you link becomes the
      // home (fk) so it shows on the row AND the map's home-based attribution.
      const makeHome = asHome || !live.place;
      // Only ever send place ids that STILL EXIST — a stale/deleted id left in
      // item.places would make the whole PATCH 400 (OwnedPKField "does not
      // exist"), silently breaking every future link on this expense.
      const known = new Set([...places.map((p) => p.id), pl.id]);
      const extra = (live.places || []).filter((id) => id !== pl.id && known.has(id));
      if (makeHome) {
        if (live.place && live.place !== pl.id && known.has(live.place)) extra.push(live.place);
        await api(`/budget/items/${live.id}/`,
                  { method: 'PATCH', body: { place: pl.id, places: extra } });
      } else if (!extra.includes(pl.id)) {
        await api(`/budget/items/${live.id}/`,
                  { method: 'PATCH', body: { places: [...extra, pl.id] } });
      }
      setSearch(''); setSearchResults(null); setPendingPlace(null); onChanged();
    } catch (e) {
      setError(e.data?.detail || e.message || 'Could not link that place — try again.');
    }
  };

  const savePlace = async (cand) => {
    setBusy(true); setError('');
    try {
      // Reuse an existing place with the same name, else create it.
      let place = places.find((p) => p.name.toLowerCase() === cand.name.toLowerCase());
      if (!place) {
        place = await api('/budget/places/', { method: 'POST',
          body: { ...cand, kind: cand.source === 'address' ? 'address' : 'shop' } });
        setPlaces((ps) => [...ps, place]);
      }
      if (aMode === 'visit') {
        // JUST THIS SPEND: a dated visit — the expense keeps (or still
        // lacks) its constant home; the money/units land all the same.
        if (!(await applyVisit(place, assigning))) { setBusy(false); return; }
      } else {
        await api(`/budget/items/${assigning.id}/`,
                  { method: 'PATCH', body: { place: place.id } });
        if (alsoRecurring) {
          // Stick to the recurring twin (same title) so future months auto-place.
          const rec = await api('/budget/recurring/');
          const twin = rec.find((t) => t.kind === 'expense'
            && t.title.trim().toLowerCase() === assigning.title.trim().toLowerCase());
          if (twin) await api(`/budget/recurring/${twin.id}/`,
                              { method: 'PATCH', body: { place: place.id } });
        }
      }
      setAssigning(null); setCandidates(null); setPendingPlace(null);
      setSearch(''); setSearchResults(null);
      onChanged();
    } catch (e) {
      setError(e.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink flex flex-col md:flex-row"
         data-guide-context="map">
      {/* map */}
      <div className="flex-1 flex flex-col min-w-0">
        {!dropMode && (
          <div className="shrink-0 bg-panel border-b border-edge px-3 py-2 flex flex-col gap-2 text-xs relative z-[400]">
            <div className="flex items-center gap-2 w-full">
            <span className="font-bold text-sm text-white px-1 mr-auto">
              📍 Where your money goes
            </span>
            </div>
            {/* The one list search — filters the by-place list; dictation comes
                from the shared SearchField (native type=search clears it). */}
            <SearchField value={q} onChange={setQ} onVoice={setQ} size={36} parse={false}
                         placeholder="Search places, events or expenses…" />
            {/* AI read-back — idle states the search reach; with a query it
                echoes what was heard and how many places match, closest first. */}
            {(() => {
              const _n = places.filter((p) => mq(p, true) && placeHasActivity(p)
                && (lens === 'coming' ? comingByPlace(p.id) > 0 : placeUsage(p.id).spent > 0)).length;
              return (
                <div className="text-[11px] leading-snug text-slate-500 px-1 -mt-0.5">
                  {q.trim()
                    ? `\u{1F399} Heard "${q.trim()}" \u2014 showing ${_n} match${_n === 1 ? '' : 'es'}. Say a place, a category or an amount to narrow it further.`
                    : 'Searching finds places, standing events and the expenses linked to them \u2014 the branch nearest you ranks first.'}
                </div>
              );
            })()}
            <div className="flex items-center gap-2 w-full flex-wrap">
            {/* SPENT vs UPCOMING lens — the whole-month view flips between money
                already SPENT and the spend still COMING (each shop's still-to-pay).
                Picking a day drops the lens (a day only replays real spend). */}
            {!byDate && (
              <div className="flex items-center rounded-full border border-edge overflow-hidden">
                {[['spent', 'Spent'], ['coming', 'Coming']].map(([k, label]) => (
                  <button key={k}
                          className={`px-2.5 py-1 ${
                            lens === k
                              ? (k === 'coming' ? 'bg-vblue/25 text-vblue' : 'bg-vpurple/25 text-vpurple')
                              : 'text-slate-400'}`}
                          onClick={() => setLens(k)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button className={`px-2.5 py-1 rounded-full border ${
                      !byDate ? 'border-vblue bg-vblue/15 text-white'
                              : 'border-edge text-slate-400'}`}
                    data-guide="map-daterange"
                    onClick={() => setByDate('')}>
              Whole month
            </button>
            {/* Sync locations — pull each expense's shop from last month for any
                expense that has no location yet (idempotent; a match-less or
                already-placed expense is left alone). */}
            <button className="px-2.5 py-1 rounded-full border border-vpurple/60 text-vpurple
                               hover:bg-vpurple/15 transition disabled:opacity-50"
                    data-guide="map-syncplaces"
                    disabled={syncing}
                    title="Fill this month's map from last month — link each expense to the location it used before"
                    onClick={syncPlaces}>
              {syncing ? '⟳ Syncing…' : '⟳ Sync locations'}
            </button>
            {/* Which pins to show — synced across web, mobile and the watches. */}
            <div className="flex items-center rounded-full border border-edge overflow-hidden"
                 data-guide="map-pinpref"
                 title="Show every saved place, or only places with a linked expense/event/visit">
              {[['all', 'All places'], ['linked', 'Linked only']].map(([k, label]) => (
                <button key={k}
                        className={`px-2.5 py-1 ${mapPins === k
                          ? 'bg-vpurple/25 text-vpurple' : 'text-slate-400'}`}
                        onClick={() => setPinPref(k)}>
                  {label}
                </button>
              ))}
            </div>
            {/* Bank chips — scope pins + both lists to one account, the same
                single-select, click-to-clear chips as the dashboard. PER SURFACE
                view state: unlike lens/range/pins it is never persisted. Only
                shown once there is more than one account to choose between. */}
            {bankChips.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap" data-guide="map-bankfilter"
                   title="Show only the pins and rows on one account">
                <button className={`px-2.5 py-1 rounded-full border ${
                          !bankFilter ? 'border-vblue bg-vblue/15 text-white' : 'border-edge text-slate-400'}`}
                        onClick={() => setBankFilter(0)}>
                  All banks
                </button>
                {bankChips.map(([id, name]) => (
                  <button key={id}
                          className={`px-2.5 py-1 rounded-full border ${
                            bankFilter === id ? 'border-vblue bg-vblue/15 text-white' : 'border-edge text-slate-400'}`}
                          onClick={() => setBankFilter(bankFilter === id ? 0 : id)}>
                    {name}
                  </button>
                ))}
              </div>
            )}
            <span className="text-base leading-none" title="Pick a day to replay">🗓</span>
            <input type="date" className="input !py-1 !px-2 !w-36 text-xs"
                   title="Replay a day — pins and lists show only that day's spending"
                   value={byDate} onChange={(e) => setByDate(e.target.value)} />
            {byDate && [['shop', 'By place'], ['expense', 'By expense'], ['all', 'All']]
              .map(([k, label]) => (
              <button key={k}
                      className={`px-2 py-1 rounded-full border ${
                        byView === k ? 'border-vpurple bg-vpurple/15 text-vpurple'
                                     : 'border-edge text-slate-400'}`}
                      onClick={() => setByView(k)}>
                {label}
              </button>
            ))}
            </div>
          </div>
        )}
        <div className="relative flex-1 min-h-0 m-3 rounded-2xl overflow-hidden border border-edge">
        <div ref={mapEl} className="absolute inset-0" />
        <div className="absolute top-3 left-3 z-[500] flex flex-col gap-1 pointer-events-none">
          {areaLabel && <div className="text-[10px] text-slate-300 bg-panel/85 border border-edge rounded px-2 py-1">◱ Area in view · {areaLabel}</div>}
          <div className="text-[10px] text-slate-400 bg-panel/80 border border-edge rounded px-2 py-1">bubble size = amount</div>
        </div>
        <button className="absolute top-4 right-4 z-[1000] w-10 h-10 rounded-full bg-panel
                           border border-edge text-slate-300 hover:text-white shadow-lg"
                onClick={onClose}>
          ✕
        </button>
        {dropMode && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-panel border
                          border-vpurple rounded-full px-4 py-2 text-sm shadow-lg">
            Click the map to place "{assigning?.title}"
          </div>
        )}
        {/* THE DAY BAR — floating on the map itself, top centre: whole
            month, or any day (with its three list views). Pins follow. */}

        </div>
        <div className="shrink-0 px-3 py-2 border-t border-edge">
        {/* Stat tiles (mockup): places with a linked expense, total spent,
            biggest place, and the closest one. */}
        {!byDate && places.length > 0 && (() => {
          const active = places.filter((pl) => placeHasActivity(pl));
          const spentBy = active.map((pl) => ({ pl, s: placeUsage(pl.id).spent }));
          const totalSpent = spentBy.reduce((a, x) => a + x.s, 0);
          const linkedCount = active.filter((pl) => expenses.some((i) => (i.place === pl.id || (i.places || []).includes(pl.id)) && onBank(i))).length;
          const biggest = spentBy.slice().sort((a, b) => b.s - a.s)[0];
          const withDist = active.map((pl) => ({ pl, d: distToPlace(pl) })).filter((x) => isFinite(x.d)).sort((a, b) => a.d - b.d)[0];
          const tiles = [
            ['Places', String(linkedCount), 'with a linked expense'],
            ['Spent', fmtR(totalSpent), 'this month'],
            ['Biggest', biggest && biggest.s > 0 ? fmtR(biggest.s) : '—', biggest && biggest.s > 0 ? biggest.pl.name : ''],
            ['Closest', withDist ? fmtKm(withDist.d) : '—', withDist ? withDist.pl.name : 'no location fix'],
          ];
          return (
            <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))' }}>
              {tiles.map(([k, v, sub]) => (
                <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-3 py-2">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">{k}</div>
                  <div className="text-sm font-bold text-slate-100 truncate">{v}</div>
                  <div className="text-[9px] text-slate-500 truncate">{sub}</div>
                </div>
              ))}
            </div>
          );
        })()}
        </div>
      </div>

      {/* side panel */}
      <div className="w-full md:w-[26rem] xl:w-[30rem] bg-panel border-l border-edge overflow-y-auto overflow-x-hidden p-5">
        <MapBoundary>
        {/* The title + day bar float ON THE MAP (top centre) — the panel
            stays about lists and actions. */}

            {!byDate && rules.some((r) => !(budget.items || []).find((x) => x.kind === 'expense'
              && (x.title || '').toLowerCase() === (r.item_title || '').toLowerCase())?.completed) && (
              <div className="mt-5" data-guide="map-events">
                <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  ⟳ Daily shop events
                </h3>
                <div className="grid gap-1.5">
                  {rules.filter((r) => !(budget.items || []).find((x) => x.kind === 'expense'
                    && (x.title || '').toLowerCase() === (r.item_title || '').toLowerCase())?.completed)
                    .map((r) => {
                    // A DONE expense's event is hidden here (settled for the month);
                    // Clicking a daily shop event opens the SAME expense editor as
                    // "this month by expense" (Basic/Advanced/Logged), with this
                    // event's shop as context — so you can log a visit or edit its
                    // cadence/units/amount inline on Advanced (the ② section's
                    // "More" still opens the rule-only editor). An orphan rule with
                    // no matching expense falls back to that rule editor.
                    const rExp = (budget.items || []).find((x) => x.kind === 'expense'
                      && (x.title || '').toLowerCase() === (r.item_title || '').toLowerCase());
                    const rPlace = places.find((p) => p.id === r.place) || null;
                    const openRule = () => setEditRule({ id: r.id,
                      item_title: r.item_title, place_name: r.place_name, place: r.place,
                      units: r.units || '', amount: r.units ? '' : r.amount,
                      cadence: r.cadence, day_of_month: r.day_of_month || 1,
                      times_per_day: r.times_per_day || 1,
                      days_of_week: r.days_of_week || '',
                      active: r.active });
                    return (
                    <div key={r.id} className="flex items-center gap-1">
                    <button className="flex-1 min-w-0 flex items-center gap-2 border border-edge rounded-xl
                                       px-3 py-2 text-sm text-left hover:border-vblue transition"
                            title="Open — log a visit, or edit units/rands, daily/monthly, pause or stop"
                            onMouseEnter={() => { if (rPlace) previewOnMap(rPlace); }}
                            onMouseLeave={clearPreview}
                            onClick={() => { if (rPlace) previewOnMap(rPlace);
                              if (rExp) startAssign(rExp, rPlace); else openRule(); }}>
                      <span className="flex-1 truncate">
                        {r.item_title}
                        <span className="text-xs text-slate-500">
                          {' '}@ {r.place_name} — {r.times_per_day > 1
                            ? `${r.times_per_day}× ` : ''}{r.units
                            ? `${r.units}× unit${r.units === 1 ? '' : 's'}`
                            : fmtR(r.amount)}
                          {r.cadence === 'monthly'
                            ? `/month · day ${r.day_of_month || 1}`
                            : r.cadence === 'weekly'
                            ? ` · ${['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][r.day_of_week || 1]}`
                            : r.cadence === 'geo' ? ' · when there' : '/day'}
                        </span>
                        {(() => {
                          const ri = (budget.items || []).find((x) => x.kind === 'expense'
                            && (x.title || '').toLowerCase()
                               === (r.item_title || '').toLowerCase());
                          const vs2 = monthVisits.filter((v) => v.place === r.place
                            && v.confirmed && (ri ? v.item === ri.id
                              : (v.title || '').toLowerCase()
                                 === (r.item_title || '').toLowerCase()));
                          const du = vs2.reduce((a, v) => a + Number(v.units || 0), 0);
                          const ds = vs2.reduce((a, v) => a + Number(v.amount || 0), 0);
                          return (du || ds) ? (
                            <span className="block text-[10px] text-emerald-400">
                              done: {du ? `${du}× units` : ''}{du && ds ? ' · ' : ''}{ds ? fmtR(ds) : ''}
                            </span>
                          ) : (
                            <span className="block text-[10px] text-slate-600">none logged yet</span>
                          );
                        })()}
                      </span>
                      <span className={`text-[10px] uppercase ${
                              rExp?.completed ? 'text-emerald-400'
                                : r.active ? 'text-vpurple' : 'text-slate-600'}`}>
                        {rExp?.completed ? '✓ done' : r.active ? '⟳ on' : 'paused'}
                      </span>
                      <span className="text-slate-600">›</span>
                    </button>
                      <button className="text-slate-600 hover:text-red-400 shrink-0 px-1.5 py-2"
                              title="Stop this standing event"
                              onClick={() => deleteRule(r)}>✕</button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!byDate && places.filter((p) => mq(p, true) && placeHasActivity(p)
                && (lens === 'coming' ? comingByPlace(p.id) > 0 : placeUsage(p.id).spent > 0)).length > 0 && (
              <div className="mt-5" data-guide="map-places">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-wide text-slate-500">
                    {lens === 'coming' ? 'Coming spend by place' : 'This month by place'}
                  </h3>
                  <div className="flex items-center gap-1 text-[10px]">
                    {[['amount', 'Amount'], ['distance', 'Distance'], ['name', 'Name']].map(([k, label]) => (
                      <button key={k} type="button" onClick={() => setMapSort(k)}
                              className={`px-2 py-0.5 rounded-full border transition ${mapSort === k ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-500 hover:border-slate-400'}`}>{label}</button>
                    ))}
                  </div>
                </div>
                <div ref={placeListRef} className="grid gap-1.5 relative">
                  {places
                    .filter((p) => mq(p, true) && placeHasActivity(p)
                      && (lens === 'coming' ? comingByPlace(p.id) > 0 : placeUsage(p.id).spent > 0))
                    .slice()
                    .sort((a, b) => {
                      if (mapSort === 'name') return (a.name || '').localeCompare(b.name || '');
                      if (mapSort === 'distance') return distToPlace(a) - distToPlace(b);
                      return lens === 'coming'
                        ? comingByPlace(b.id) - comingByPlace(a.id)
                        : (spendByPlace[b.id] || 0) - (spendByPlace[a.id] || 0);
                    })
                    .map((p) => (
                      <div key={p.id} id={`web-place-${p.id}`}
                           className={`group border ${openList === p.id ? 'border-vpurple bg-vpurple/5' : 'border-edge'}
                                       rounded-xl px-3 py-2 ${
                                         (lens === 'coming' ? comingByPlace(p.id) : spendByPlace[p.id]) ? '' : 'opacity-50'}`}
                           onMouseEnter={() => previewOnMap(p)}
                           onMouseLeave={clearPreview}>
                        <div className="flex items-center gap-2">
                        {/* Click the SHOP to open its editor: rename, and see
                            every expense linked here — each editable onward
                            with the full Basic/Advanced options. */}
                        <button className="flex-1 text-sm font-bold text-left hover:text-vblue transition"
                                title="Click to see what was spent here; use ✎ to rename or move the pin"
                                onClick={() => { previewOnMap(p); acc.toggle('place:' + p.id); }}>
                          {p.name}
                          <span className={`ml-1.5 text-[8px] uppercase tracking-wide px-1 py-0.5 rounded ${
                            placeKind(p) === 'home' ? 'text-emerald-400 border border-emerald-400/40'
                            : placeKind(p) === 'address' ? 'text-amber-400 border border-amber-400/40'
                            : 'text-vpurple border border-vpurple/40'}`}>
                            {placeKind(p)}
                          </span>
                          {(() => {
                            const u = placeUsage(p.id);
                            const bud = expenses.filter((i) => i.place === p.id)
                              .reduce((a, i) => a + Number(i.amount || 0), 0);
                            if (!u.spent && !u.units) {
                              // Nothing HOMED here — but expenses linked as EXTRA
                              // locations may have spent (counted at their home),
                              // so show that rather than a misleading "no spend".
                              const linked = expenses
                                .filter((i) => (i.places || []).includes(p.id))
                                .reduce((a, i) => {
                                  let s = Number(i.actual || 0) + (Number(i.reduce_n) || 0) * unitOf(i);
                                  if (s <= 0 && i.completed) s = Number(i.amount || 0);
                                  return a + s;
                                }, 0);
                              return (
                                <span className="ml-1.5 text-[10px] text-slate-600">
                                  {linked > 0
                                    ? `${fmtR(linked)} spent here — counted at its home`
                                    : 'no spend this month'}
                                </span>
                              );
                            }
                            const left = bud - u.spent;
                            const expCount = expenses.filter((i) => i.place === p.id
                              || (i.places || []).includes(p.id)).length;
                            return (
                              <span className="block text-[10px] text-slate-500">
                                spent {fmtR(u.spent)}
                                {left < 0
                                  ? <span className="text-red-400"> · over by {fmtR(-left)}</span>
                                  : <span> · {expCount} expense{expCount === 1 ? '' : 's'}</span>}
                              </span>
                            );
                          })()}
                        </button>
                        <div className="text-right shrink-0 cursor-pointer" title="Expand / collapse this place"
                             onClick={() => { previewOnMap(p); acc.toggle('place:' + p.id); }}>
                          <div className="text-sm font-semibold text-vpurple">{fmtR(placeUsage(p.id).spent)}</div>
                          {placeVisits(p.id) > 0 && (
                            <div className="text-[9px] text-vpurple/80">{placeVisits(p.id)}× visit{placeVisits(p.id) === 1 ? '' : 's'}</div>
                          )}
                          {fmtKm(distToPlace(p)) && (
                            <div className="text-[9px] text-slate-500">{fmtKm(distToPlace(p))}</div>
                          )}
                        </div>
                        <button className="text-xs text-slate-500 hover:text-vblue opacity-0 group-hover:opacity-100 transition"
                                title="Move this pin — search its new address"
                                onClick={() => { setMoving(p); setSearch('');
                                                 setSearchResults(null);
                                                 setAssigning(null);
                                                 setCandidates(null);
                                                 setLinking(null); }}>
                          📍
                        </button>
                        <button className="text-xs text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                                title="Delete this place — linked expenses become unplaced"
                                onClick={async () => {
                                  if (await ask({
                                    title: `Delete place "${p.name}"?`,
                                    message: 'Its pin and visit history go; expenses '
                                      + 'linked to it become unplaced.',
                                    ok: 'Delete', danger: true, cancel: 'Keep it',
                                  })) {
                                    await api(`/budget/places/${p.id}/`,
                                              { method: 'DELETE' });
                                    setPlaces((ps) => ps.filter((x) => x.id !== p.id));
                                    onChanged();
                                  }
                                }}>
                          ✕
                        </button>
                        </div>
                        {(() => {
                          // Spent vs budget bar for this place (mockup card).
                          const _u = placeUsage(p.id).spent;
                          const _bud = expenses.filter((i) => i.place === p.id).reduce((a, i) => a + Number(i.amount || 0), 0);
                          if (_bud <= 0) return null;
                          const _pct = Math.max(0, Math.min(100, (_u / _bud) * 100));
                          return (
                            <div className="h-1 rounded-full bg-white/10 overflow-hidden mt-1.5">
                              <div className={`h-full ${_u > _bud ? 'bg-red-400' : 'bg-vpurple'}`} style={{ width: `${_pct}%` }} />
                            </div>
                          );
                        })()}
                        {(() => {
                          if (openList !== p.id) return null;
                          // Every expense at this shop — homed here, an extra
                          // location, rule-linked, or visited this month.
                          const _by = new Map();
                          for (const x of expenses)
                            if (x.place === p.id || (x.places || []).includes(p.id)) _by.set(x.id, x);
                          const _titles = new Set(rules.filter((r) => r.place === p.id)
                            .map((r) => (r.item_title || '').toLowerCase()).filter(Boolean));
                          if (_titles.size) for (const x of expenses)
                            if (_titles.has((x.title || '').toLowerCase())) _by.set(x.id, x);
                          for (const v of monthVisits)
                            if (v.place === p.id && v.confirmed && v.item) {
                              const x = expenses.find((e) => e.id === v.item);
                              if (x) _by.set(x.id, x);
                            }
                          const here = [..._by.values()];
                          const uHere = placeUsage(p.id);
                          const vcount = monthVisits.filter((v) => v.place === p.id).length;
                          const detailSub = `spent ${fmtR(uHere.spent)}${fmtKm(distToPlace(p)) ? ` · ${fmtKm(distToPlace(p))}` : ''}${vcount > 0 ? ` · ${vcount} visit${vcount === 1 ? '' : 's'}` : ''}`;
                          return (
                            <div className="mt-2 flex flex-col gap-2.5 border-t border-edge/60 pt-2.5" data-guide="place-detail">
                              <div className="text-xs text-slate-500">{detailSub}</div>
                              {here.length > 0 && (
                                <div className="flex flex-col gap-2">
                                  {here.map((i) => {
                                    const rate = unitOf(i);
                                    const rn = Number(i.reduce_n) || 0;
                                    const spd = (Number(i.actual) || 0) + rn * rate;
                                    const shown = spd > 0 ? spd : (Number(i.amount) || 0);
                                    const budgetUnits = rate > 0 ? Math.round((Number(i.amount) || 0) / rate) : 0;
                                    const vc = monthVisits.filter((v) => v.confirmed && (String(v.item) === String(i.id) || (v.title || '').toLowerCase() === (i.title || '').toLowerCase())).length;
                                    return (
                                      <div key={i.id} className="rounded-xl border border-edge px-3 py-2.5">
                                        <div className="flex items-baseline gap-2">
                                          <button className="flex-1 min-w-0 text-left text-sm font-medium truncate hover:text-vblue transition"
                                                  title="Open this expense — full edit" onClick={() => startAssign(i, p)}>{i.title}</button>
                                          {rate > 0 && <span className="text-[12.5px] text-slate-500 shrink-0">{moneySymbol()}{rate}/unit</span>}
                                          <span className="text-[11px] text-slate-500 shrink-0"
                                                title={rn ? `${rn}× R${rate} + ${fmtR(Number(i.actual) || 0)} spent = ${fmtR(spd)}` : ''}>
                                            {budgetUnits > 0 ? `${rn} of ${budgetUnits} units · ` : ''}{fmtR(shown)}
                                          </span>
                                          {i.completed && <span className="text-[10px] text-emerald-400 shrink-0" title="Ticked done this month">✓ done</span>}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                          {rate > 0 && (
                                            <>
                                              <button className="w-11 h-11 shrink-0 rounded-full border border-edge text-slate-300 hover:border-vpurple transition text-lg leading-none"
                                                      title="One fewer unit" onClick={() => bumpUnits(i, -1, p.id)}>−</button>
                                              <span className="min-w-[26px] text-center text-base font-semibold text-vpurple">{rn}</span>
                                              <button className="w-11 h-11 shrink-0 rounded-full border border-edge text-slate-300 hover:border-vpurple transition text-lg leading-none"
                                                      title="One more unit" onClick={() => bumpUnits(i, 1, p.id)}>+</button>
                                            </>
                                          )}
                                          <form className="flex-1 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); const inp = e.currentTarget.elements.amt; const val = inp.value; if (val) { setSpend(i, val); inp.value = ''; } }}>
                                            <input name="amt" className="input flex-1 !min-h-[44px] text-sm" placeholder="R amount" inputMode="decimal"
                                                   title="Type an amount, then Log (or press Enter)" />
                                            <button type="submit" className="text-[12.5px] shrink-0 border border-vpurple/50 text-vpurple rounded-xl px-3 min-h-[44px] inline-flex items-center justify-center hover:bg-vpurple/10 transition" title="Log this spend">Log</button>
                                          </form>
                                          <button className="text-[12.5px] shrink-0 border border-edge rounded-xl px-3 min-h-[44px] inline-flex items-center justify-center hover:border-vpurple transition"
                                                  title="Log a zero-cost visit — it still counts" onClick={() => bumpUnits(i, 1, p.id)}>✓ Just visited</button>
                                        </div>
                                        {vc > 0 && <div className="text-[11px] mt-1.5 text-vpurple/80">{vc} visit{vc === 1 ? '' : 's'} logged · counts toward the place</div>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {here.length === 0 && (
                                <button className="w-full text-xs border border-vpurple/50 text-vpurple rounded-lg min-h-[44px] hover:bg-vpurple/10 transition"
                                        onClick={() => { setAssigning(null); setMoving(null); setPendingPlace(null); setLinking(p); setLinkItem(null); setAMode('home'); setLq(''); }}>＋ Link an expense here</button>
                              )}
                              <button className="w-full text-[12.5px] border border-vpurple/50 text-vpurple rounded-lg min-h-[44px] flex items-center justify-center gap-1 hover:bg-vpurple/10 transition mb-1"
                                      onClick={() => setCashPlace(p.id)}>💵 Spend cash here</button>
                              <div className="flex gap-2">
                                <button className="flex-1 text-[12.5px] border border-edge rounded-lg min-h-[44px] flex items-center justify-center hover:border-vpurple transition" onClick={() => setEditPlace(p)}>Rename</button>
                                <button className="flex-1 text-[12.5px] border border-edge rounded-lg min-h-[44px] flex items-center justify-center hover:border-vpurple transition"
                                        onClick={() => { setMoving(p); setSearch(''); setSearchResults(null); setAssigning(null); setCandidates(null); setLinking(null); }}>Move pin</button>
                                <button className="flex-1 text-[12.5px] border border-edge rounded-lg min-h-[44px] flex items-center justify-center hover:border-vpurple transition" onClick={() => setEditPlace(p)}>Standing event</button>
                                <button className="flex-1 text-[12.5px] border border-edge rounded-lg min-h-[44px] flex items-center justify-center hover:border-vpurple transition" onClick={() => { setMergeQ(''); setMergeFrom(p); }}>Merge</button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                </div>
              </div>
            )}
            {!byDate && places.length > 0 && places.filter((p) => mq(p, true) && placeHasActivity(p)
                && (lens === 'coming' ? comingByPlace(p.id) > 0 : placeUsage(p.id).spent > 0)).length === 0 && (
              <div className="mt-5 flex flex-col items-center text-center gap-2 py-8" data-guide="map-empty">
                <span className="text-3xl opacity-20">🔒</span>
                <div className="text-sm font-semibold text-slate-300">Nothing matches this lens</div>
                <div className="text-xs text-slate-500 max-w-xs">
                  Nothing in {mapPins === 'linked' ? 'Linked only' : 'All places'} matches{' '}
                  {q.trim() ? `"${q.trim()}"` : 'this'} under {lens === 'coming' ? 'Coming' : 'Spent'}.{' '}
                  Try {lens === 'coming' ? 'Spent' : 'Coming'}, or All places.
                </div>
                <button className="mt-1 text-xs border border-vpurple/50 text-vpurple rounded-lg min-h-[44px] px-4 hover:bg-vpurple/10 transition"
                        onClick={() => { setQ(''); setLens('spent'); setPinPref('all'); setBankFilter(0); }}>
                  Clear the filters
                </button>
              </div>
            )}
        {/* Search any store or address — assign directly, or just pin it. */}
        <div className="mt-3" data-guide="map-search">
          <div className="flex gap-2">
            <input className="input !py-2 text-sm" value={search}
                   placeholder={assigning
                     ? `Search a place for "${assigning.title}"…`
                     : moving
                       ? `New address for "${moving.name}"…`
                       : 'Search a store or address — results as you type…'}
                   onChange={(e) => setSearch(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
            <button className="btn-primary !py-2 !px-3 text-sm" onClick={runSearch}
                    disabled={busy}>
              🔍
            </button>
            <button className="!py-2 !px-3 text-sm rounded-xl border border-edge
                               text-vblue hover:text-white shrink-0"
                    title="Recenter the map on my location" onClick={recenterOnMe}>
              ◎
            </button>
            <VoiceButton onText={(t) => setSearch(t)} title="Search shops by voice" />
            <button data-guide="map-pin-here"
                    className="!py-2 !px-3 text-sm rounded-xl border border-edge
                               text-vblue hover:text-white shrink-0"
                    title="Pin where you are as a saved place (Home, work, anywhere)"
                    onClick={pinMyLocation}>🏠</button>
            <button data-guide="map-trip"
                    className={`!py-2 !px-3 text-sm rounded-xl border shrink-0 ${
                      trip ? 'border-emerald-400 text-emerald-400 animate-pulse'
                           : 'border-edge text-emerald-400 hover:text-white'}`}
                    title={trip ? 'End the trip and add its cost' : 'Start tracking a trip (Uber/drive)'}
                    onClick={trip ? endTrip : startTrip}>
              {trip ? '⏹ End' : '🚗'}
            </button>
            <button data-guide="map-trips"
              className={`!py-2 !px-3 text-sm rounded-xl border shrink-0 ${
                tripsOpen ? 'border-vblue text-white' : 'border-edge text-vblue hover:text-white'}`}
              title="See past trips — the road you drove, distance and cost"
              onClick={() => { const nx = !tripsOpen; setTripsOpen(nx); if (nx) loadTrips(); else setSelTrip(null); }}>
              🧭 Trips
            </button>
            {tripsOpen && (
              <div className="fixed z-[1200] top-24 right-6 w-72 max-h-[62vh] overflow-y-auto card !p-3 shadow-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm">🚗 Your trips</span>
                  <button className="text-slate-400 hover:text-white text-sm"
                          onClick={() => { setTripsOpen(false); setSelTrip(null); }}>✕</button>
                </div>
                {trips.length === 0 ? (
                  <p className="text-xs text-slate-500">No trips yet. Tap 🚗 to track one — the road, distance and cost are saved here.</p>
                ) : trips.map((t) => (
                  <button key={t.id}
                          className={`w-full text-left rounded-lg border p-2 mb-2 transition ${
                            selTrip && selTrip.id === t.id ? 'border-emerald-400 bg-emerald-400/10' : 'border-edge hover:border-slate-500'}`}
                          onClick={() => { setSelTrip(t); setRouteShown(true); }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold truncate">{t.label || 'Trip'}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        {t.is_ride ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-400/20 text-emerald-300">🚕 ride</span> : null}
                        {t.business ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-vpurple/20 text-vpurple">{taxPackAvailable() ? 'SARS' : 'Business'}</span> : null}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {Number(t.distance_km || 0).toFixed(1).replace('.', ',')} km{t.cost != null ? ` · R ${Number(t.cost).toFixed(2)}` : ' · no cost'}{t.client_name ? ` · ${t.client_name}` : ''}
                    </div>
                    {t.ended_at ? <div className="text-[10px] text-slate-500 mt-0.5">{String(t.ended_at).slice(0, 10)}</div> : null}
                  </button>
                ))}
              </div>
            )}
            {/* Saved-trip / Paid-ride card — the finished trip: distance, an
                optional taxi / e-hailing fare that logs as a real Transport
                spend, and a Show-the-route toggle (hidden by default). */}
            {savedTrip && (
              <div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center bg-black/50 p-4"
                   onClick={() => confirmTrip(false)}>
                <div className="card w-full max-w-sm !p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-base">🚗 {savedTrip.label || 'Trip'}</span>
                    <button className="text-slate-400 hover:text-white text-sm"
                            title="Save as a trip and close" onClick={() => confirmTrip(false)}>✕</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div className="rounded-lg border border-edge p-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Distance</div>
                      <div className="text-sm font-semibold tabular-nums">{Number(savedTrip.distance_km || 0).toFixed(1).replace('.', ',')} km</div>
                    </div>
                    <div className="rounded-lg border border-edge p-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">{rideMode ? 'Fare' : 'Cost'}</div>
                      <div className="text-sm font-semibold tabular-nums">{rideMode && rideFare ? fmtR(Number(rideFare)) : '—'}</div>
                    </div>
                    <div className="rounded-lg border border-edge p-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Date</div>
                      <div className="text-sm font-semibold">{String(savedTrip.ended_at || savedTrip.created_at || new Date().toISOString()).slice(0, 10)}</div>
                    </div>
                  </div>

                  <button className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 mb-2 transition ${
                            rideMode ? 'border-emerald-400 text-emerald-300 bg-emerald-400/10' : 'border-edge text-slate-300'}`}
                          onClick={() => { const nx = !rideMode; setRideMode(nx); if (nx && !rideItem) setRideItem(rideGuess()); }}>
                    <span className="text-sm font-medium">🚕 Paid ride — taxi / e-hailing</span>
                    <span className={`w-9 h-5 rounded-full relative transition shrink-0 ${rideMode ? 'bg-emerald-400' : 'bg-slate-600'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${rideMode ? 'left-4' : 'left-0.5'}`} />
                    </span>
                  </button>

                  {rideMode ? (
                    <div className="grid gap-2 mb-3">
                      <label className="block">
                        <span className="text-[11px] text-slate-500">Fare paid ({moneySymbol()})</span>
                        <input className="input !py-2 text-sm w-full mt-0.5" inputMode="decimal"
                               placeholder="e.g. 76.00" value={rideFare}
                               onChange={(e) => setRideFare(e.target.value)} />
                      </label>
                      <label className="block">
                        <span className="text-[11px] text-slate-500">Which expense</span>
                        <select className="input !py-2 text-sm w-full mt-0.5"
                                value={rideItem} onChange={(e) => setRideItem(e.target.value)}>
                          <option value="">— pick a transport expense —</option>
                          {expenses.map((ex) => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
                        </select>
                      </label>
                      <p className="text-[11px] text-emerald-400">
                        Logs {rideFare ? fmtR(Number(rideFare)) : 'the fare'} as a real spend and drops a start → end marker on the map. Stop and restart keeps it one record. The route stays hidden until you tap Show the route.
                      </p>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-500 mb-3">
                      Saved as a trip, not an expense — petrol logged against a fuel expense is separate. This feeds the travel logbook and the route on the map.
                    </p>
                  )}

                  <button className="w-full rounded-xl border border-vblue/50 text-vblue text-sm py-2 mb-2 hover:bg-vblue/10 transition"
                          onClick={() => setRouteShown((v) => !v)}>
                    🗺 {routeShown ? 'Hide the route' : 'Show the route on the map'}
                  </button>

                  <div className="flex gap-2">
                    <button className="flex-1 !py-2 text-sm rounded-xl border border-edge text-slate-300 hover:text-white"
                            onClick={() => confirmTrip(false)} disabled={rideBusy}>Save as trip</button>
                    {rideMode && (
                      <button className="flex-1 btn-primary !py-2 text-sm" onClick={() => confirmTrip(true)}
                              disabled={rideBusy || !rideFare}>
                        {rideBusy ? 'Saving…' : 'Log the fare'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(search || searchResults || assigning || moving) && (
              <button className="!py-2 !px-3 text-sm rounded-xl border border-edge
                                 text-slate-400 hover:text-white shrink-0"
                      title="Clear the search and any shop/expense in progress"
                      onClick={() => { setSearch(''); setSearchResults(null);
                        setCandidates(null); setAssigning(null); setMoving(null);
                        setLinking(null); setLinkItem(null); setError('');
                        clearPreview(); recenterOnMe(); }}>
                ✕ Clear
              </button>
            )}
          </div>
          {/* Typing an expense name here surfaces YOUR expenses too (not only
              Google shops), so the one search box finds both. */}
          {search.trim().length >= 2 && (() => {
            const q2 = search.trim().toLowerCase();
            const hits = expenses.filter((i) => (i.title || '').toLowerCase().includes(q2)).slice(0, 5);
            if (!hits.length) return null;
            return (
              <div className="mt-2 grid gap-1.5">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Your expenses</div>
                {hits.map((i) => (
                  <button key={i.id}
                          className="flex items-center gap-2 border border-edge rounded-xl
                                     px-3 py-2 text-left hover:border-vblue transition"
                          onClick={() => startAssign(i)}>
                    <span className="flex-1 text-sm truncate">{i.title}</span>
                    <span className="text-vblue text-xs shrink-0">place →</span>
                  </button>
                ))}
              </div>
            );
          })()}
          {searchResults && (
            <div className="mt-2 grid gap-1.5" data-guide="map-results">
              {searchResults.candidates.map((c, i) => (
                <button key={i} className="text-left border border-edge rounded-xl p-2.5
                                           hover:border-vpurple transition"
                        onMouseEnter={() => previewOnMap(c)}
                        onMouseLeave={clearPreview}
                        onClick={() => pickSearchResult(c)}>
                  <span className="text-sm font-medium">{c.name || search.trim()}</span>
                  <span className={`ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded ${
                    c.source === 'address' ? 'text-amber-400 border border-amber-400/40'
                                           : 'text-vpurple border border-vpurple/40'}`}>
                    {c.source === 'address' ? 'address' : 'shop'}
                  </span>
                  {fmtKm(c.distance_km) && (
                    <span className="ml-2 text-[10px] text-vpurple">{fmtKm(c.distance_km)} away</span>
                  )}
                  <span className="block text-xs text-slate-500">{c.address}</span>
                  <span className="text-[10px] text-vblue">
                    {assigning
                      ? (aMode === 'visit'
                          ? `log the dated spend for "${assigning.title}" here →`
                          : `make this "${assigning.title}"'s home →`)
                      : moving ? `move "${moving.name}" here →`
                      : 'save — then pick the expense →'}
                  </span>
                </button>
              ))}
              {!searchResults.candidates.length && (
                <p className="text-xs text-slate-500">
                  {searchResults.note ? searchResults.note
                    : searchResults.geocoding
                    ? 'No matches — try adding the suburb or city.'
                    : 'Geocoding is not configured — drop pins manually for now.'}
                </p>
              )}
            </div>
          )}
        </div>
        {moving && (
          <p className="text-xs text-amber-400 mt-1.5">
            Moving “{moving.name}” — pick its new address above.{' '}
            <button className="underline" onClick={() => setMoving(null)}>cancel</button>
          </p>
        )}
        {linking && createPortal(<MapBoundary>
          {/* The address chose a shop — NOW choose the expense FIRST, so the
             options know its formula; then Basic (home) or Advanced (dated,
             units/rands, once/daily/monthly). A TRUE overlay: centred, over
             everything. */}
          <div className="fixed inset-0 z-[75] bg-black/75 backdrop-blur-[2px]
                          flex items-center justify-center p-4"
               >
          <div className="card w-full max-w-md max-h-[88vh] overflow-y-auto" data-guide="map-link-panel">
            {!linkItem ? (
              <>
                {/* The NAME is yours even when the geocoder christened it —
                    edit it right here; the pin keeps its spot. */}
                <div className="flex items-center gap-2">
                  <input className="input !py-1.5 text-sm flex-1"
                         key={`ln${linking.id}:${linking.name}`}
                         defaultValue={linking.name}
                         title="The shop's name — call it what YOU call it (saved on blur/Enter)"
                         onBlur={async (e) => {
                           const v = e.target.value.trim();
                           if (v && v !== linking.name) {
                             const up = await api(`/budget/places/${linking.id}/`,
                               { method: 'PATCH',
                                 body: { name: v.slice(0, 60) } });
                             setPlaces((ps) => ps.map((x) =>
                               (x.id === linking.id ? up : x)));
                             setLinking(up);
                           }
                         }}
                         onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                  <button className="w-7 h-7 rounded-full border border-edge text-slate-400
                                     hover:text-white text-xs shrink-0"
                          onClick={() => setLinking(null)}>✕</button>
                </div>
                <p className="text-sm font-medium mt-2">
                  Saved — which expense fits here?
                </p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <input className="input !py-1.5 text-sm flex-1"
                         placeholder="🔎 Type to find the expense…"
                         value={lq} onChange={(e) => setLq(e.target.value)} />
                  <VoiceButton onText={(t) => setLq(t)} title="Find the expense by voice" />
                </div>
                <div className="grid gap-1 mt-2 max-h-44 overflow-y-auto">
                  {[...unplaced, ...expenses.filter((i) => i.place)]
                    .filter((i) => !lq.trim()
                      || i.title.toLowerCase().includes(lq.trim().toLowerCase()))
                    .map((i) => (
                    <button key={i.id}
                            className={`flex items-center gap-2 text-left border border-edge
                                        rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue
                                        transition ${i.place ? 'opacity-60' : ''}`}
                            onClick={() => { setLinkItem(i); setAMode('home');
                                             setError(''); }}>
                      <span className="flex-1 truncate">{i.title}</span>
                      {unitOf(i) > 0 && (
                        <span className="text-[10px] text-amber-400">{moneySymbol()}{unitOf(i)}/unit</span>
                      )}
                      <span className="text-xs text-slate-500">{fmtR(i.actual ?? i.amount)}</span>
                    </button>
                  ))}
                </div>
                {/* Create a NEW expense homed here — not just link an existing one,
                    so a pin is never a dead end (mirrors the watches + mobile). */}
                <div className="mt-3 border-t border-edge pt-2.5">
                  <p className="text-xs text-slate-400 mb-1.5">Or add a new expense here</p>
                  <div className="flex items-center gap-1.5">
                    <input className="input !py-1.5 text-sm flex-1" placeholder="Expense name"
                           value={newHereTitle} onChange={(e) => setNewHereTitle(e.target.value)} />
                    <input className="input !py-1.5 text-sm !w-24" placeholder="Amount" inputMode="decimal"
                           value={newHereAmt} onChange={(e) => setNewHereAmt(e.target.value)} />
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    <button className="btn-primary flex-1 text-xs !py-1.5"
                            disabled={busy || !newHereTitle.trim()
                              || !(Number(String(newHereAmt).replace(/[rR\s,]/g, '')) > 0)}
                            onClick={() => createExpenseHere(false)}>Add to budget</button>
                    <button className="flex-1 text-xs py-1.5 rounded-lg border border-vpurple/50
                                       text-vpurple hover:bg-vpurple/10 disabled:opacity-50"
                            disabled={busy || !newHereTitle.trim()
                              || !(Number(String(newHereAmt).replace(/[rR\s,]/g, '')) > 0)}
                            onClick={() => createExpenseHere(true)}>Log as spent</button>
                  </div>
                </div>
                <button className="mt-2 text-xs text-slate-500"
                        onClick={() => setLinking(null)}>
                  Just keep the pin — no expense
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">
                  “{linkItem.title}” @ “{linking.name}”
                  <button className="ml-2 text-xs text-slate-500 underline"
                          onClick={() => setLinkItem(null)}>change</button>
                  <button className="ml-2 text-xs text-red-400/80 underline"
                          title="Cancel — nothing is saved"
                          onClick={() => { setLinking(null); setLinkItem(null);
                            setVUnits(''); setVAmt(''); setVRepeat('once');
                            setError(''); }}>✕ cancel</button>
                </p>
                <div className="flex gap-1.5 mt-2">
                  <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                            aMode === 'home' ? 'border-vblue bg-vblue/15 text-white'
                                             : 'border-edge text-slate-500'}`}
                          onClick={() => setAMode('home')}>
                    🏠 Its home
                  </button>
                  <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                            aMode === 'visit' ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                                              : 'border-edge text-slate-500'}`}
                          onClick={() => setAMode('visit')}>
                    📅 Dated & recurring
                  </button>
                </div>
                {aMode === 'home' ? (
                  <button className="btn-primary w-full !py-2 mt-2 text-sm"
                          onClick={async () => {
                            setError('');
                            try {
                              await api(`/budget/items/${linkItem.id}/`,
                                        { method: 'PATCH',
                                          body: { place: linking.id } });
                              setLinking(null); setLinkItem(null); onChanged();
                            } catch (e) {
                              setError(e.data?.detail || e.message);
                            }
                          }}>
                    Make “{linking.name}” its constant home
                  </button>
                ) : (
                  <>
                    <div className="flex gap-1.5 mt-1.5 items-center">
                      {unitOf(linkItem) > 0 && (
                        <input className="input !py-1.5 !w-24 text-sm"
                               placeholder={`units ×R${unitOf(linkItem)}`}
                               value={vUnits}
                               onChange={(e) => setVUnits(e.target.value)} />
                      )}
                      <input className="input !py-1.5 flex-1 min-w-0 text-sm"
                             placeholder="R/unit — or flat" value={vAmt}
                             onChange={(e) => setVAmt(e.target.value)} />
                      <input type="date" className="input !py-1.5 !w-36 text-sm" value={vDate}
                             onChange={(e) => setVDate(e.target.value)} />
                    </div>
                    {repeatChips(linkItem)}
                    <button className="btn-primary w-full !py-2 mt-2 text-sm"
                            onClick={async () => {
                              setError('');
                              try {
                                if (!(await applyVisit(linking, linkItem))) return;
                                setLinking(null); setLinkItem(null); onChanged();
                              } catch (e) {
                                setError(e.data?.detail || e.message);
                              }
                            }}>
                      Log it {!(mapGeo || mapCad.cadence !== 'none') ? '' : `— and repeat ${mapGeo ? 'when there' : mapCad.cadence}`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          </div>
        </MapBoundary>, document.body)}
        {byDate && (() => {
          // The chosen day, EXPENSE-first: under each highlighted shop, ONE
          // line per expense (not one-per-visit). Click it to open the expense
          // — a "Visits" tab to confirm each of the day's occurrences, and a
          // "Setup" tab showing how many visits a day are configured.
          const cellMap = {};
          for (const v of visits) {
            const k = `${v.place}|${v.item || 't:' + (v.title || '')}`;
            if (!cellMap[k]) cellMap[k] = { place: v.place, place_name: v.place_name,
              item: v.item, title: v.title, n: 0, pending: 0, total: 0 };
            const c = cellMap[k];
            c.n += 1;
            if (v.confirmed) c.total += Number(v.amount || 0); else c.pending += 1;
          }
          // Also surface recurring events DUE on this day that have no visit
          // yet — so every shop/expense expected today appears (both shops),
          // ready to log or confirm, without waiting for the 23:30 run.
          const dayNum = Number(byDate.slice(8, 10)) || 1;
          for (const r of rules) {
            if (!r.active || r.cadence === 'geo') continue;
            const due = r.cadence === 'daily'
              || (r.cadence === 'monthly' && dayNum === (r.day_of_month || 1));
            if (!due) continue;
            const it2 = (budget.items || []).find((x) => x.kind === 'expense'
              && (x.title || '').toLowerCase() === (r.item_title || '').toLowerCase());
            const k = `${r.place}|${it2 ? it2.id : 't:' + (r.item_title || '')}`;
            if (cellMap[k]) continue;
            cellMap[k] = { place: r.place, place_name: r.place_name,
              item: it2 ? it2.id : null, title: r.item_title,
              n: 0, pending: 0, total: 0, expected: r.times_per_day || 1 };
          }
          const cells = Object.values(cellMap);
          const cellRow = (c) => (
            <div key={`${c.place}|${c.item || c.title}`}
                 className="flex items-center gap-2 text-sm py-1"
                 onMouseEnter={() => previewOnMap(places.find((x) => x.id === c.place))}
                 onMouseLeave={clearPreview}>
              <button className="flex-1 min-w-0 text-left truncate hover:text-vblue transition"
                      title="Open this expense — confirm the day's visits, or see its setup"
                      onClick={() => { setDayExpense(c); setDeTab('visits'); }}>
                {byView === 'expense' ? (c.place_name || 'No place linked — tap to fix') : (c.title || 'spend')}
                {c.n > 1 && <span className="text-[10px] text-slate-500"> · {c.n}×</span>}
                {c.pending > 0 && (
                  <span className="text-[10px] text-amber-400"> · {c.pending} to confirm</span>
                )}
                {c.expected && !c.n && (
                  <span className="text-[10px] text-amber-400"> · {c.expected}× expected today</span>
                )}
              </button>
              <span className="font-medium text-vpurple shrink-0">{fmtR(c.total)}</span>
              <span className="text-slate-600">›</span>
            </div>
          );
          const groups = {};
          const keyOf = (c) => (byView === 'shop' ? (c.place_name || '—') : (c.title || 'spend'));
          if (byView !== 'all') {
            for (const c of cells) (groups[keyOf(c)] = groups[keyOf(c)] || []).push(c);
          }
          const total = visits.filter((v) => v.confirmed)
            .reduce((s, v) => s + Number(v.amount || 0), 0);
          const pending = visits.filter((v) => !v.confirmed).length;
          return (
            <div className="mt-2 border border-edge rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <h3 className="text-xs uppercase tracking-wide text-slate-500 flex-1">
                  Spent on {byDate} — {fmtR(total)}
                  {pending > 0 && <span className="text-amber-400"> · {pending} to confirm</span>}
                </h3>
                <button className="text-[10px] px-2 py-0.5 rounded-full border border-edge
                                   text-slate-400 hover:text-white hover:border-vblue shrink-0"
                        title="Clear the day — show the whole month and all expenses again"
                        onClick={() => setByDate('')}>
                  ✕ Show all
                </button>
              </div>
              {!visits.length && (
                <p className="text-xs text-slate-600">No visits logged on this day yet.</p>
              )}
              {byView === 'all'
                ? cells.map(cellRow)
                : Object.entries(groups).map(([g, cs]) => (
                    <div key={g} className="mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <button className="flex-1 min-w-0 truncate font-semibold text-left
                                           text-white hover:text-vblue transition"
                                title={byView === 'shop'
                                  ? 'Open this shop — rename, its expenses, its events'
                                  : 'Open this expense'}
                                onClick={() => {
                                  if (byView === 'shop') {
                                    const p = places.find((x) => x.id === cs[0]?.place)
                                      || places.find((x) => x.name === g);
                                    if (p) setEditPlace(p);
                                  } else {
                                    setDayExpense(cs[0]); setDeTab('visits');
                                  }
                                }}>
                          {byView === 'shop' ? '📍 ' : ''}{g}
                          <span className="text-slate-600"> ›</span>
                        </button>
                        <span className="text-vpurple font-semibold shrink-0">
                          {fmtR(cs.reduce((s, c) => s + c.total, 0))}
                        </span>
                      </div>
                      {cs.map(cellRow)}
                    </div>
                  ))}
            </div>
          );
        })()}
        {dayExpense && createPortal(<MapBoundary>{(() => {
          const de = dayExpense;
          const occ = visits.filter((v) => v.place === de.place
            && (de.item ? v.item === de.item
                        : (v.title || '').toLowerCase() === (de.title || '').toLowerCase()));
          const rule = rules.find((r) => r.place === de.place
            && (r.item_title || '').toLowerCase() === (de.title || '').toLowerCase());
          const it = (budget.items || []).find((x) => x.id === de.item)
            || (budget.items || []).find((x) => x.kind === 'expense'
                 && (x.title || '').toLowerCase() === (de.title || '').toLowerCase());
          const per = rule && rule.units
            ? `${rule.units}× unit${rule.units === 1 ? '' : 's'}`
            : rule ? fmtR(rule.amount) : '';
          const nPending = occ.filter((v) => !v.confirmed).length;
          return (
          <div className="fixed inset-0 z-[78] bg-black/75 backdrop-blur-[2px]
                          flex items-center justify-center p-4">
          <div className="card w-full max-w-md max-h-[88vh] overflow-y-auto" data-guide="map-expense">
            <div className="flex items-start gap-3">
              <h3 className="font-semibold text-sm flex-1">
                {de.title}
                <span className="block text-xs text-slate-500 font-normal">@ {de.place_name}</span>
              </h3>
              <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                 hover:text-white shrink-0"
                      onClick={() => setDayExpense(null)}>✕</button>
            </div>
            <div className="flex gap-1.5 mt-3">
              {[['visits', `Visits · ${byDate}`], ['setup', 'Setup']].map(([k, label]) => (
                <button key={k}
                        className={`flex-1 text-xs py-1.5 rounded-xl border ${
                          deTab === k ? 'border-vblue bg-vblue/15 text-white'
                                      : 'border-edge text-slate-500'}`}
                        onClick={() => setDeTab(k)}>
                  {label}
                </button>
              ))}
            </div>
            {deTab === 'visits' ? (
              <div className="mt-3">
                {!occ.length && (
                  <p className="text-xs text-slate-600">No visits for this day yet.</p>
                )}
                {occ.map((v, i) => (
                  <div key={v.id}
                       className="flex items-center gap-2 text-sm py-1.5 border-b border-edge/50">
                    <span className="flex-1 min-w-0 truncate">
                      Visit {i + 1}
                      <span className="text-[10px] text-amber-400">
                        {' '}{v.units > 0
                          ? `${v.units}× unit${v.units === 1 ? '' : 's'}` : fmtR(v.amount)}
                      </span>
                      {!v.confirmed && <span className="text-[10px] text-amber-400"> · coming</span>}
                    </span>
                    <span className="text-vpurple font-medium shrink-0">{fmtR(v.amount)}</span>
                    {v.confirmed ? (
                      <span className="text-[10px] text-emerald-400 shrink-0">✓ logged</span>
                    ) : (
                      <button className="text-[10px] px-2 py-0.5 rounded-full border border-vblue
                                         text-vblue hover:bg-vblue/10 shrink-0"
                              onClick={() => confirmVisits([v.id])}>
                        I was there
                      </button>
                    )}
                    <button className="text-xs text-slate-600 hover:text-red-400 shrink-0"
                            title={v.confirmed ? 'Delete — reverses on the expense'
                                               : 'Remove this expected visit'}
                            onClick={() => deleteVisits([v.id])}>✕</button>
                  </div>
                ))}
                {nPending > 0 && (
                  <button className="btn-primary w-full !py-2 mt-3 text-sm"
                          onClick={() => confirmVisits(occ.filter((v) => !v.confirmed).map((v) => v.id))}>
                    I was there — confirm all {nPending}
                  </button>
                )}
                {it && unitOf(it) > 0 && (
                  <div className="mt-3 border-t border-edge pt-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">
                      ① Root — this expense’s own count (no shop visit)
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                         hover:border-vblue shrink-0"
                              onClick={() => bumpUnits(it, -1)}>−</button>
                      <span className="text-sm flex-1 text-center">
                        {Number(it.reduce_n) || 0}× unit{(Number(it.reduce_n) || 0) === 1 ? '' : 's'}
                        <span className="text-slate-500"> · {fmtR((Number(it.reduce_n) || 0) * unitOf(it))}</span>
                      </span>
                      <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                         hover:border-vblue shrink-0"
                              onClick={() => bumpUnits(it, 1)}>+</button>
                    </div>
                  </div>
                )}
                {rule && (
                  <div className="mt-3 border-t border-edge pt-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">
                      ② Shop event — one visit at its value · {per}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                         hover:border-vblue shrink-0"
                              title="Reverse the last logged visit here"
                              onClick={async () => {
                                const mine = occ.filter((v) => v.confirmed);
                                if (mine.length) {
                                  const last = mine.reduce((a, b) => (b.id > a.id ? b : a));
                                  await deleteVisits([last.id]);
                                }
                              }}>−</button>
                      <span className="text-[11px] text-slate-400 flex-1 text-center">
                        one visit · {per}
                      </span>
                      <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                         hover:border-vblue shrink-0"
                              title="Log one visit at the event’s defined value"
                              onClick={async () => {
                                if (it) await addVisit(it, de.place,
                                  { units: rule.units || 0,
                                    amount: rule.units ? 0 : rule.amount });
                              }}>+</button>
                    </div>
                  </div>
                )}
                <div className="mt-3 border-t border-edge pt-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    ③ Or a custom spend — any units or R
                  </div>
                  {it && unitOf(it) > 0 ? (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* Formula: step the unit count, OR log a plain spend. */}
                      <div className="flex items-center gap-2">
                        <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                           hover:border-vblue disabled:opacity-30 shrink-0"
                                disabled={(Number(deUnits) || 1) <= 1}
                                onClick={() => { setDeUnits(String(Math.max(1,
                                  (Number(deUnits) || 1) - 1))); setDeAmt(''); }}>−</button>
                        <span className="text-sm text-center min-w-[70px]">
                          {Number(deUnits) || 1}× unit{(Number(deUnits) || 1) === 1 ? '' : 's'}
                          <span className="block text-[10px] text-slate-500">{moneySymbol()}{unitOf(it)} each</span>
                        </span>
                        <button className="w-8 h-8 rounded-full border border-edge text-slate-300
                                           hover:border-vblue shrink-0"
                                onClick={() => { setDeUnits(String((Number(deUnits) || 1) + 1));
                                                 setDeAmt(''); }}>+</button>
                      </div>
                      <input className="input !py-1.5 !w-24 text-sm" placeholder="or R spend"
                             value={deAmt}
                             onChange={(e) => { setDeAmt(e.target.value); setDeUnits(''); }} />
                      <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                              disabled={!it}
                              onClick={async () => {
                                await addVisit(it, de.place,
                                  { units: deAmt ? 0 : (Number(deUnits) || 1),
                                    amount: deAmt ? deAmt : 0 });
                                setDeUnits(''); setDeAmt('');
                              }}>
                        + Add
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <input className="input !py-1.5 flex-1 text-sm" placeholder="R amount"
                             value={deAmt}
                             onChange={(e) => setDeAmt(e.target.value)} />
                      <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                              disabled={!it}
                              onClick={async () => {
                                await addVisit(it, de.place, { units: 0, amount: deAmt });
                                setDeAmt('');
                              }}>
                        + Add visit
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-2">
                  A visit counts only once you confirm it (or add one here). On
                  mobile, being at the shop confirms it for you.
                </p>
              </div>
            ) : (
              <div className="mt-3">
                {rule ? (
                  <>
                    <p className="text-sm">
                      Happens{' '}
                      <span className="text-white font-semibold">
                        {rule.times_per_day || 1}× a day
                      </span>{' '}
                      ({rule.cadence}
                      {rule.cadence === 'monthly' ? ` · day ${rule.day_of_month || 1}` : ''}),
                      each {per}.
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      That many pending visits are recorded at day's end for you to
                      confirm.{rule.active ? '' : ' Currently paused.'}
                    </p>
                    <button className="btn-primary w-full !py-2 mt-3 text-sm"
                            onClick={() => { setEditRule({ id: rule.id,
                              item_title: rule.item_title, place_name: rule.place_name, place: rule.place,
                              units: rule.units || '', amount: rule.units ? '' : rule.amount,
                              cadence: rule.cadence, day_of_month: rule.day_of_month || 1,
                              times_per_day: rule.times_per_day || 1,
                              days_of_week: rule.days_of_week || '',
                              active: rule.active }); setDayExpense(null); }}>
                      Edit the recurring setup — times/day, units, pause, stop
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-400">
                      “{de.title}” isn't set to repeat at {de.place_name} yet.
                    </p>
                    <button className="btn-primary w-full !py-2 mt-3 text-sm"
                            disabled={!it}
                            onClick={() => { if (it) { setDayExpense(null);
                              setVAmt(''); setVUnits(''); setVRepeat('daily');
                              setAMode('visit'); startAssign(it); } }}>
                      Make it recurring here…
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          </div>
          );
        })()}</MapBoundary>, document.body)}

        {/* PORTALS: every overlay renders onto document.body — over the
            map, the panel, everything, always centred. */}
        {assigning && !dropMode && createPortal(<MapBoundary>{(() => {
          const curPlace = places.find((p) => p.id === assigning.place) || assignCtx || null;
          return (
          /* THE OVERLAY — a mapped (or unmapped) expense opens like every
             other Vault object: card in the middle, tabs inside, ✕ to
             leave. While dropping a pin the overlay steps aside so the map
             is clickable; the pick brings it back to confirm. */
          <div className="fixed inset-0 z-[75] bg-black/75 backdrop-blur-[2px]
                          flex items-center justify-center p-4">
          <div className="card w-full max-w-md max-h-[88vh] overflow-y-auto">
            <div className="flex items-start gap-3">
              <h3 className="font-semibold text-sm flex-1">{assigning.title}</h3>
              <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                 hover:text-white shrink-0"
                      onClick={() => { setAssigning(null); setPendingPlace(null);
                                       setCandidates(null); }}>✕</button>
            </div>
            {(() => {
              const live = (budget.items || []).find((x) => x.id === assigning.id) || assigning;
              return <DetailCard item={live} onChange={onChanged}
                                 categories={live.category ? [{ id: live.category, name: live.category_name || 'Category' }] : []}
                                 banks={live.bank ? [{ id: live.bank, name: live.bank_name || 'Bank' }] : []}
                                 onOpenMap={() => setDropMode(true)} showHeader={false} />;
            })()}
            {(<>
            {(() => {
              // WHERE IT'S SPENT — every place this expense touches: its home
              // shop, extra locations, plus shops it only reaches through a
              // standing event or a logged visit. Home and non-home alike.
              const live = (budget.items || []).find((x) => x.id === assigning.id) || assigning;
              const extra = live.places || [];
              const home = places.find((x) => x.id === live.place) || null;
              const titleL = (live.title || '').toLowerCase();
              const eventIds = rules.filter((r) => (r.item_title || '').toLowerCase() === titleL)
                .map((r) => r.place);
              const visitIds = monthVisits.filter((v) => String(v.item) === String(live.id)
                || (v.title || '').toLowerCase() === titleL).map((v) => v.place);
              const linkedSet = new Set([live.place, ...extra].filter(Boolean));
              const looseIds = [...new Set([...eventIds, ...visitIds].filter(Boolean))]
                .filter((id) => !linkedSet.has(id));
              const taken = new Set([...linkedSet, ...looseIds]);
              const addable = places.filter((pl) => !taken.has(pl.id));
              const knownIds = new Set(places.map((p) => p.id));
              const patchItem = async (body) => {
                // Never let a stale/deleted place id in item.places 400 the whole
                // PATCH — that silently broke re-homing/unlinking. Surface a real
                // error instead of swallowing it, so a failed link is never silent.
                const clean = Array.isArray(body.places)
                  ? { ...body, places: body.places.filter((id) => knownIds.has(id)) }
                  : body;
                try {
                  await api(`/budget/items/${live.id}/`, { method: 'PATCH', body: clean });
                } catch (e) {
                  setError(e.data?.detail || 'Could not update its locations — try again.');
                }
                onChanged();
              };
              const promote = (id) => patchItem({ place: id,
                places: [...extra.filter((x) => x !== id),
                         ...(live.place && live.place !== id ? [live.place] : [])] });
              const kindBadge = (pl) => (placeKind(pl) === 'address'
                ? <span className="text-[9px] uppercase text-amber-400 border border-amber-400/40 rounded px-1 shrink-0">address</span>
                : <span className="text-[9px] uppercase text-vpurple border border-vpurple/40 rounded px-1 shrink-0">shop</span>);
              return (
                <div className="mt-2 border border-edge rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                    Where it's spent — shops &amp; addresses linked to it
                  </div>
                  {home ? (
                    <button className="w-full flex items-center gap-2 text-left group"
                            title="Tap to unhome — keep it as an extra location or unlink it"
                            onClick={() => askUnhome(live)}>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-vblue/15 text-vblue
                                       border border-vblue/40 shrink-0
                                       group-hover:bg-red-400/15 group-hover:text-red-400
                                       group-hover:border-red-400/40">🏠 Home</span>
                      <span className="flex-1 min-w-0 truncate text-sm font-medium">{home.name}</span>
                      {kindBadge(home)}
                      <span className="text-[11px] text-slate-500 group-hover:text-red-400 shrink-0">✕ unhome</span>
                    </button>
                  ) : (
                    <div className="text-[11px] text-slate-500">
                      No home shop yet — tap 🏠 on a location below, or search to add one.
                    </div>
                  )}
                  {extra.length > 0 && (
                    <div className="grid gap-1 mt-2">
                      {extra.map((id) => {
                        const pl = places.find((x) => x.id === id);
                        if (!pl) return null;
                        return (
                          <div key={id}
                               className="flex items-center gap-2 min-w-0 text-[11px] border border-edge rounded-lg px-2 py-1.5">
                            <span className="text-[9px] uppercase text-slate-500 border border-edge rounded px-1 shrink-0">location</span>
                            <span className="flex-1 min-w-0 truncate">📍 {pl.name}</span>
                            {kindBadge(pl)}
                            <button className="text-vblue hover:text-white px-1 shrink-0"
                                    title="Make this the home shop" onClick={() => promote(id)}>🏠</button>
                            <button className="text-slate-500 hover:text-red-400 px-1 shrink-0"
                                    title="Unlink this shop"
                                    onClick={() => patchItem({ places: extra.filter((x) => x !== id) })}>✕</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {looseIds.length > 0 && (
                    <div className="grid gap-1 mt-2">
                      {looseIds.map((id) => {
                        const pl = places.find((x) => x.id === id);
                        if (!pl) return null;
                        const viaEvent = eventIds.includes(id);
                        return (
                          <div key={`loose${id}`}
                               className="flex items-center gap-2 min-w-0 text-[11px] border border-dashed border-edge rounded-lg px-2 py-1.5">
                            <span className="text-[9px] uppercase text-slate-500 border border-edge rounded px-1 shrink-0">
                              {viaEvent ? 'event' : 'visited'}
                            </span>
                            <span className="flex-1 min-w-0 truncate">📍 {pl.name}</span>
                            {kindBadge(pl)}
                            <button className="text-vblue hover:text-white px-1 shrink-0"
                                    title="Make this the home shop" onClick={() => promote(id)}>🏠</button>
                            <button className="text-slate-500 hover:text-vblue px-1 shrink-0"
                                    title="Keep it as a linked location"
                                    onClick={() => patchItem({ places: [...extra, id] })}>＋</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {addable.length > 0 && (
                    <select className="input !py-1.5 text-sm mt-2 w-full" value=""
                            onChange={(e) => { const id = Number(e.target.value);
                              if (id) patchItem({ places: [...extra, id] }); }}>
                      <option value="">＋ Add a saved shop as another location…</option>
                      {addable.map((pl) => (
                        <option key={pl.id} value={pl.id}>{pl.name}</option>
                      ))}
                    </select>
                  )}
                  <p className="text-[10px] text-slate-600 mt-1.5">
                    Search below — pick “🏠 Make home” or “＋ Add as location”.
                  </p>
                </div>
              );
            })()}
            {!pendingPlace && (
              <>
                <div className="flex items-center gap-1.5 mt-2">
                  <input className="input !py-1.5 text-sm flex-1"
                         placeholder={curPlace
                           ? 'Search to RE-HOME it or spend elsewhere…'
                           : 'Search a store or address — results as you type…'}
                         value={search} onChange={(e) => setSearch(e.target.value)} />
                  <VoiceButton onText={(t) => setSearch(t)} title="Search shop or address by voice" />
                </div>
                {search.trim().length >= 1 && (() => {
                  // YOUR shops that match come FIRST — the ones you already
                  // know before any fresh Google result underneath them.
                  const ql = search.trim().toLowerCase();
                  const linkedIds = new Set(expenses.filter((x) => x.place).map((x) => x.place));
                  const mine = places.filter((p) =>
                    (p.name || '').toLowerCase().includes(ql)
                    || (p.address || '').toLowerCase().includes(ql))
                    // Nearby list: never suggest a shop past 20 km (command 4).
                    // With no location fix we cannot measure, so keep them all.
                    .filter((p) => !posRef.current || withinNearby(distToPlace(p)))
                    // Closest shop first (parity with mobile + watch); an
                    // already-linked shop breaks a tie so a known place still wins.
                    .sort((a, b) => (distToPlace(a) - distToPlace(b))
                      || (linkedIds.has(b.id) ? 1 : 0) - (linkedIds.has(a.id) ? 1 : 0))
                    .slice(0, 8);
                  if (!mine.length) return null;
                  return (
                    <div className="mt-1.5 grid gap-1.5">
                      <span className="text-[10px] uppercase tracking-wide text-vblue">Your shops</span>
                      {mine.map((p) => (
                        <div key={`mine${p.id}`}
                             className="border border-vblue/40 rounded-xl p-2.5"
                             onMouseEnter={() => previewOnMap(p)}
                             onMouseLeave={clearPreview}>
                          <span className="text-sm font-medium">📍 {p.name}</span>
                          {fmtKm(distToPlace(p)) && (
                            <span className="ml-2 text-[10px] text-vpurple">{fmtKm(distToPlace(p))} away</span>
                          )}
                          <span className="block text-xs text-slate-500">{p.address}</span>
                          <div className="flex gap-1.5 mt-1.5">
                            <button className="flex-1 text-[11px] py-1 rounded-lg border border-vblue/50 text-vblue hover:bg-vblue/10"
                                    onClick={() => linkCandidate(p, true)}>🏠 Make home</button>
                            <button className="flex-1 text-[11px] py-1 rounded-lg border border-edge text-slate-300 hover:border-vblue"
                                    onClick={() => linkCandidate(p, false)}>＋ Add as location</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {searchResults && (
                  <div className="mt-1.5 grid gap-1.5">
                    {searchResults.candidates.map((c, i) => (
                      <div key={i}
                           className="border border-edge rounded-xl p-2.5"
                           onMouseEnter={() => previewOnMap(c)}
                           onMouseLeave={clearPreview}>
                        <span className="text-sm font-medium">{c.name}</span>
                        <span className={`ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded ${
                          c.source === 'address' ? 'text-amber-400 border border-amber-400/40'
                                                 : 'text-vpurple border border-vpurple/40'}`}>
                          {c.source === 'address' ? 'address' : 'shop'}
                        </span>
                        {fmtKm(c.distance_km) && (
                          <span className="ml-2 text-[10px] text-vpurple">
                            {fmtKm(c.distance_km)} away
                          </span>
                        )}
                        <span className="block text-xs text-slate-500">{c.address}</span>
                        <div className="flex gap-1.5 mt-1.5">
                          <button className="flex-1 text-[11px] py-1 rounded-lg border border-vblue/50 text-vblue hover:bg-vblue/10"
                                  onClick={() => linkCandidate(c, true)}>🏠 Make home</button>
                          <button className="flex-1 text-[11px] py-1 rounded-lg border border-edge text-slate-300 hover:border-vpurple"
                                  onClick={() => linkCandidate(c, false)}>＋ Add as location</button>
                        </div>
                      </div>
                    ))}
                    {!searchResults.candidates.length && (
                      <p className="text-xs text-slate-500">
                        {searchResults.note || 'No matches — add the suburb.'}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {pendingPlace ? (
              /* THE CONFIRM STEP — nothing applied yet. The pick is shown,
                 the tabs decide what happens, a button makes it real. */
              <div className="mt-2 border border-vpurple/50 rounded-xl p-3">
                {/* The NAME is yours, not AI's/the map's — edit it right
                    here before anything saves. */}
                <div className="flex items-center gap-2">
                  <input className="input !py-1.5 text-sm flex-1"
                         title="Call the shop what YOU call it — editable before saving"
                         value={pendingPlace.name}
                         onChange={(e) => setPendingPlace({
                           ...pendingPlace, name: e.target.value.slice(0, 60) })} />
                  <button className="text-xs text-slate-500 underline shrink-0"
                          onClick={() => setPendingPlace(null)}>change place</button>
                  <button className="text-xs text-red-400/80 underline shrink-0"
                          title="Cancel — nothing is saved"
                          onClick={() => { setAssigning(null); setPendingPlace(null);
                            setCandidates(null); setVUnits(''); setVAmt('');
                            setVRepeat('once'); setError(''); }}>✕ cancel</button>
                </div>
                <input className="input !py-1 text-xs mt-1 w-full"
                       placeholder="Address (editable)"
                       title="The address — fix or refine it before saving"
                       value={pendingPlace.address || ''}
                       onChange={(e) => setPendingPlace({
                         ...pendingPlace, address: e.target.value.slice(0, 200) })} />
                <PinPreview lat={pendingPlace.lat} lng={pendingPlace.lng} />
                <div className="flex gap-1.5 mt-2">
                  <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                            aMode === 'home' ? 'border-vblue bg-vblue/15 text-white'
                                             : 'border-edge text-slate-500'}`}
                          onClick={() => setAMode('home')}>
                    🏠 Its home
                  </button>
                  <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                            aMode === 'visit' ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                                              : 'border-edge text-slate-500'}`}
                          onClick={() => setAMode('visit')}>
                    📅 Dated & recurring
                  </button>
                </div>
                {aMode === 'home' ? (
                  <>
                    <label className="flex items-center gap-2 text-xs text-slate-400 mt-2">
                      <input type="checkbox" checked={alsoRecurring}
                             onChange={() => setAlsoRecurring(!alsoRecurring)} />
                      Also link my recurring "{assigning.title}" — future months
                      place themselves
                    </label>
                    <button className="btn-primary w-full !py-2 mt-2 text-sm"
                            onClick={() => savePlace(pendingPlace)}>
                      Make “{pendingPlace.name}” its constant home
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex gap-1.5 mt-1.5 items-center">
                      {unitOf(assigning) > 0 && (
                        <input className="input !py-1.5 !w-24 text-sm"
                               placeholder={`units ×R${unitOf(assigning)}`}
                               title={`Units of this expense's formula — R${unitOf(assigning)} each`}
                               value={vUnits}
                               onChange={(e) => setVUnits(e.target.value)} />
                      )}
                      <input className="input !py-1.5 flex-1 min-w-0 text-sm"
                             placeholder="R/unit — or flat"
                             value={vAmt}
                             onChange={(e) => setVAmt(e.target.value)} />
                      <input type="date" className="input !py-1.5 !w-36 text-sm" value={vDate}
                             onChange={(e) => setVDate(e.target.value)} />
                    </div>
                    {repeatChips(assigning)}
                    <button className="btn-primary w-full !py-2 mt-2 text-sm"
                            onClick={() => savePlace(pendingPlace)}>
                      Log it {!(mapGeo || mapCad.cadence !== 'none') ? '' : `— and repeat ${mapGeo ? 'when there' : mapCad.cadence}`}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Pick a place — from the suggestions below, your own places, the
                  search box above, or a dropped pin. NOTHING applies until you
                  confirm on the next step.
                </p>
                {busy && <p className="text-slate-500 text-sm mt-2">AI is finding it…</p>}
                {candidates && !searchResults && (
                  <div className="mt-2 grid gap-2">
                    {candidates.candidates.map((c, i) => (
                      <div key={i} className="border border-edge rounded-xl p-3"
                           onMouseEnter={() => previewOnMap(c)}
                           onMouseLeave={clearPreview}>
                        <span className="font-medium text-sm">{c.name}</span>
                        {fmtKm(c.distance_km) && (
                          <span className="ml-2 text-[10px] text-vpurple">{fmtKm(c.distance_km)} away</span>
                        )}
                        <span className="block text-xs text-slate-500">{c.address}</span>
                        <div className="flex gap-1.5 mt-1.5">
                          <button className="flex-1 text-[11px] py-1 rounded-lg border border-vblue/50 text-vblue hover:bg-vblue/10"
                                  onClick={() => linkCandidate(c, true)}>🏠 Make home</button>
                          <button className="flex-1 text-[11px] py-1 rounded-lg border border-edge text-slate-300 hover:border-vpurple"
                                  onClick={() => linkCandidate(c, false)}>＋ Add as location</button>
                        </div>
                      </div>
                    ))}
                    {!candidates.candidates.length && (
                      <p className="text-xs text-slate-500">
                        {candidates.note ? candidates.note
                          : candidates.geocoding
                          ? 'No matches — search above or drop the pin yourself.'
                          : `AI suggests "${candidates.query}" — geocoding isn't configured, so drop the pin yourself.`}
                      </p>
                    )}
                    {places.length > 0 && (
                      <div className="grid gap-1">
                        <span className="text-[10px] uppercase tracking-wide text-slate-600">
                          or one of your existing places
                        </span>
                        {places.slice().sort((a, b) => distToPlace(a) - distToPlace(b)).map((p) => (
                          <button key={p.id}
                                  className="text-left border border-edge rounded-xl px-2.5 py-1.5
                                             text-sm hover:border-vblue transition"
                                  onClick={() => setPendingPlace({
                                    name: p.name, address: p.address,
                                    lat: p.lat, lng: p.lng })}>
                            📍 {p.name}
                            {fmtKm(distToPlace(p)) && (
                              <span className="ml-2 text-[10px] text-vpurple">{fmtKm(distToPlace(p))} away</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    <button className={`border rounded-xl py-2 text-sm transition ${
                              dropMode ? 'border-vpurple text-vpurple' : 'border-edge text-slate-400'}`}
                            onClick={() => setDropMode(!dropMode)}>
                      📍 {dropMode ? 'Click the map…' : 'Drop the pin on the map myself'}
                    </button>
                  </div>
                )}
              </>
            )}
            </>)}
            {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
          </div>
          </div>
          );
        })()}</MapBoundary>, document.body)}
        {(
          <>
            {/* Search-as-you-type across BOTH lists: unplaced expenses and
                the month's places — same live filter as everywhere else. */}
            {!byDate && (unplaced.length > 0 || places.length > 0) && (
              <div className="flex gap-2 mt-4">
                <input className="input !py-1.5 text-sm flex-1"
                       placeholder="🔎 Search expenses & places…"
                       value={q} onChange={(e) => setQ(e.target.value)} />
                <VoiceButton onText={(t) => setQ(t)} title="Search expenses & places by voice" />
              </div>
            )}
            {!byDate && unplaced.filter(mq).length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Not on the map yet
                </h3>
                <div className="grid gap-1.5">
                  {unplaced.filter(mq).map((i) => (
                    <button key={i.id}
                            className="flex items-center gap-2 border border-edge rounded-xl
                                       px-3 py-2 text-left hover:border-vblue transition"
                            onClick={() => startAssign(i)}>
                      <span className="flex-1 min-w-0">
                        <span className="text-sm block truncate">{i.title}</span>
                        {(() => {
                          const rate = unitOf(i);
                          const done = Number(i.reduce_n) || 0;
                          const spent = Number(i.actual || 0) + done * rate;
                          const left = Number(i.amount || 0) - spent;
                          if (!spent && !done) return null;
                          return (
                            <span className="text-[10px] text-slate-500 block">
                              spent {fmtR(spent)}{rate > 0 && done ? ` · ${done}× units` : ''}
                              {' · '}
                              <span className={left < 0 ? 'text-red-400' : 'text-emerald-400'}>
                                {left < 0 ? `over ${fmtR(-left)}` : `${fmtR(left)} left`}
                              </span>
                            </span>
                          );
                        })()}
                      </span>
                      <span className="text-xs text-slate-500 shrink-0">{fmtR(i.amount)}</span>
                      <span className="text-vblue text-xs shrink-0">place →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!byDate && placedMatches.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  On the map already
                </h3>
                <div className="grid gap-1.5">
                  {placedMatches.map((i) => (
                    <button key={i.id}
                            className="flex items-center gap-2 border border-edge rounded-xl
                                       px-3 py-2 text-left hover:border-vblue transition"
                            title="Click to re-place it or log a dated spend here"
                            onMouseEnter={() => previewOnMap(places.find((p) => p.id === i.place))}
                            onMouseLeave={clearPreview}
                            onClick={() => startAssign(i)}>
                      <span className="flex-1 text-sm truncate">
                        {i.title}
                        <span className="text-xs text-slate-500">
                          {' '}@ {(places.find((p) => p.id === i.place) || {}).name || '…'}
                        </span>
                      </span>
                      <span className="text-xs text-slate-500">{fmtR(i.actual ?? i.amount)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* #323 — merge this place into another: everything linked here moves
                to the place you pick, then this one is removed. */}
            {mergeFrom && createPortal(
              <div className="fixed inset-0 z-[1000] bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto">
                <div className="card w-full max-w-md !p-5 my-6">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h2 className="font-extrabold text-lg text-slate-100">Merge “{mergeFrom.name}” into…</h2>
                    <button className="text-slate-400 hover:text-white text-lg leading-none" onClick={() => setMergeFrom(null)} aria-label="Close">✕</button>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">Its linked expenses, logged visits, standing events, trips and cash spends move to the place you pick. “{mergeFrom.name}” is then removed.</p>
                  <input className="input w-full mb-3" placeholder="Search your places" value={mergeQ} onChange={(e) => setMergeQ(e.target.value)} autoFocus />
                  <div className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto">
                    {places
                      .filter((x) => x.id !== mergeFrom.id)
                      .filter((x) => !mergeQ.trim() || `${x.name} ${x.address || ''}`.toLowerCase().includes(mergeQ.trim().toLowerCase()))
                      .slice(0, 40)
                      .map((x) => (
                        <button key={x.id} className="text-left rounded-lg border border-edge px-3 py-2 hover:border-vpurple transition"
                                onClick={async () => {
                                  if (!await ask({ title: `Merge into “${x.name}”?`,
                                    message: `Everything linked to “${mergeFrom.name}” moves to “${x.name}”, and “${mergeFrom.name}” is removed. This can't be undone.`,
                                    ok: 'Merge', cancel: 'Cancel', danger: true })) return;
                                  try {
                                    await api(`/budget/places/${x.id}/merge/`, { method: 'POST', body: { other: mergeFrom.id } });
                                    setPlaces((ps) => ps.filter((y) => y.id !== mergeFrom.id));
                                    setMergeFrom(null);
                                    reloadDay(); reloadMonth(); onChanged();
                                  } catch { await ask({ title: 'Merge failed', message: 'Could not merge the places — try again.', ok: 'OK' }); }
                                }}>
                          <div className="text-sm text-slate-100 truncate">{x.name}</div>
                          {x.address && <div className="text-[11px] text-slate-500 truncate">{x.address}</div>}
                        </button>
                      ))}
                  </div>
                </div>
              </div>, document.body)}

            {editPlace && createPortal(<MapBoundary>{(() => {
              const p = places.find((x) => x.id === editPlace.id) || editPlace;
              const visitedHere = new Set(monthVisits
                .filter((v) => v.place === p.id && v.confirmed && v.item)
                .map((v) => v.item));
              const here = expenses.filter((i) => i.place === p.id
                || (i.places || []).includes(p.id) || visitedHere.has(i.id));
              const evHere = rules.filter((r) => r.place === p.id);
              return (
                /* THE SHOP OVERLAY — name editable, pin actions, its linked
                   expenses (each onward to Basic/Advanced) and its standing
                   events (each onward to the event overlay). */
                <div className="fixed inset-0 z-[76] bg-black/75 backdrop-blur-[2px]
                                flex items-center justify-center p-4">
                <div className="card w-full max-w-md max-h-[88vh] overflow-y-auto">
                  <div className="flex items-center gap-2">
                    <input className="input !py-1.5 text-sm flex-1"
                           key={`pn${p.id}:${p.name}`} defaultValue={p.name}
                           title="The shop's name — saved on blur/Enter"
                           onBlur={async (e) => {
                             const v = e.target.value.trim();
                             if (v && v !== p.name) {
                               const up = await api(`/budget/places/${p.id}/`,
                                 { method: 'PATCH',
                                   body: { name: v.slice(0, 60) } });
                               setPlaces((ps) => ps.map((x) =>
                                 (x.id === p.id ? up : x)));
                             }
                           }}
                           onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                    <button className="w-7 h-7 rounded-full border border-edge text-slate-400
                                       hover:text-white text-xs"
                            onClick={() => setEditPlace(null)}>✕</button>
                  </div>
                  <input className="input !py-1 text-xs mt-1 w-full"
                         key={`pa${p.id}:${p.address || ''}`}
                         placeholder="Address (editable)"
                         title="The address — fix it by hand, or 📍 Move to re-search it"
                         defaultValue={p.address || ''}
                         onBlur={async (e) => {
                           const v = e.target.value.trim();
                           if (v !== (p.address || '')) {
                             const up = await api(`/budget/places/${p.id}/`,
                               { method: 'PATCH',
                                 body: { address: v.slice(0, 200) } });
                             setPlaces((ps) => ps.map((x) =>
                               (x.id === p.id ? up : x)));
                           }
                         }}
                         onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                  <div className="flex gap-1.5 mt-2">
                    <button className="flex-1 text-xs py-1.5 rounded-xl border border-edge
                                       hover:border-vblue text-slate-400"
                            onClick={() => { setEditPlace(null); setMoving(p);
                                             setSearch(''); setSearchResults(null);
                                             setAssigning(null); setCandidates(null);
                                             setLinking(null); }}>
                      📍 Move — search its new address
                    </button>
                    <button className="flex-1 text-xs py-1.5 rounded-xl border
                                       border-red-400/50 text-red-400 hover:bg-red-400/10"
                            onClick={async () => {
                              if (await ask({
                                title: `Delete shop "${p.name}"?`,
                                message: 'Pin and visit history go; its expenses '
                                  + 'become unplaced.',
                                ok: 'Delete', danger: true, cancel: 'Keep it',
                              })) {
                                await api(`/budget/places/${p.id}/`,
                                          { method: 'DELETE' });
                                setPlaces((ps) => ps.filter((x) => x.id !== p.id));
                                setEditPlace(null); onChanged();
                              }
                            }}>
                      ✕ Delete shop
                    </button>
                  </div>
                  <h4 className="text-[10px] uppercase tracking-wide text-slate-600 mt-3 mb-1">
                    Expenses linked here
                  </h4>
                  {here.length ? here.map((i) => {
                    const isHome = String(i.place) === String(p.id);
                    return (
                    <div key={i.id}
                         className="w-full flex items-center gap-2 border border-edge
                                    rounded-lg px-2.5 py-1.5 text-sm mb-1">
                      <button className="flex-1 min-w-0 flex items-center gap-2 text-left
                                         hover:text-vblue transition"
                              title="Open it — log spend/units, change or add locations"
                              onClick={() => { setEditPlace(null); startAssign(i, p); }}>
                        <span className="flex-1 min-w-0 truncate">{i.title}</span>
                        {!isHome && <span className="text-[9px] text-slate-500 shrink-0">extra</span>}
                        {unitOf(i) > 0 && (
                          <span className="text-[10px] text-amber-400 shrink-0">{moneySymbol()}{unitOf(i)}/unit</span>
                        )}
                        {(() => {
                          // Show units DONE + total spent (units×rate + direct spend),
                          // not just the bare `actual` — that read as a lone "R200".
                          const rn = Number(i.reduce_n) || 0;
                          const u = unitOf(i);
                          const sp = (Number(i.actual) || 0) + rn * u;
                          const shown = sp > 0 ? sp : (Number(i.amount) || 0);
                          return (
                            <span className="text-xs text-slate-500 shrink-0"
                                  title={rn ? `${rn}× R${u} + ${fmtR(Number(i.actual) || 0)} spent = ${fmtR(sp)}` : ''}>
                              {rn > 0 ? `${rn}× · ` : ''}{fmtR(shown)}
                            </span>
                          );
                        })()}
                      </button>
                      <button className="text-[10px] text-slate-500 hover:text-amber-400 shrink-0
                                         border border-edge rounded-lg px-2 py-1"
                              title={isHome ? 'Unlink from its home shop' : 'Remove this extra location'}
                              onClick={async () => {
                                if (await ask({
                                  title: `Unlink “${i.title}”?`,
                                  message: `Removes ${p.name} as ${isHome ? 'its home' : 'an extra location'}. `
                                    + 'Its spend and logged visits stay.',
                                  ok: 'Unlink', cancel: 'Keep it' })) {
                                  const body = isHome
                                    ? { place: null }
                                    : { places: (i.places || []).filter((x) => x !== p.id) };
                                  await api(`/budget/items/${i.id}/`, { method: 'PATCH', body })
                                    .catch(() => {});
                                  onChanged();
                                }
                              }}>
                        unlink
                      </button>
                      <button className="text-[10px] text-slate-500 hover:text-red-400 shrink-0
                                         border border-edge rounded-lg px-2 py-1"
                              title="Delete this expense for the month"
                              onClick={async () => {
                                if (await ask({
                                  title: `Delete “${i.title}”?`,
                                  message: 'Removes this expense from the month. This cannot be undone.',
                                  ok: 'Delete', danger: true, cancel: 'Keep it' })) {
                                  await api(`/budget/items/${i.id}/?scope=month`, { method: 'DELETE' })
                                    .catch(() => {});
                                  const stillHere = here.filter((x) => x.id !== i.id);
                                  const hasRule = rules.some((r) => r.place === p.id);
                                  if (!stillHere.length && !hasRule) {
                                    await api(`/budget/places/${p.id}/`, { method: 'DELETE' }).catch(() => {});
                                    setPlaces((ps) => ps.filter((x) => x.id !== p.id));
                                    setEditPlace(null);
                                  }
                                  onChanged();
                                }
                              }}>
                        🗑
                      </button>
                    </div>
                    );
                  }) : (
                    <p className="text-xs text-slate-600">
                      Nothing linked yet — close this and pick an expense to
                      make this its home.
                    </p>
                  )}
                  {evHere.length > 0 && (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wide text-slate-600 mt-3 mb-1">
                        ⟳ Standing events at this shop
                      </h4>
                      {evHere.map((r) => (
                        <button key={r.id}
                                className="w-full flex items-center gap-2 border border-edge
                                           rounded-lg px-2.5 py-1.5 text-sm text-left
                                           hover:border-vblue transition mb-1"
                                title="Open — change units/rands, daily/monthly, pause or stop"
                                onClick={() => setEditRule({ id: r.id,
                                  item_title: r.item_title, place_name: r.place_name, place: r.place,
                                  units: r.units || '', amount: r.units ? '' : r.amount,
                                  cadence: r.cadence, day_of_month: r.day_of_month || 1,
                                  times_per_day: r.times_per_day || 1,
                                  days_of_week: r.days_of_week || '',
                                  active: r.active })}>
                          <span className="flex-1 truncate">
                            {r.item_title} — {r.times_per_day > 1
                              ? `${r.times_per_day}× ` : ''}{r.units
                              ? `${r.units}× unit${r.units === 1 ? '' : 's'}`
                              : fmtR(r.amount)}
                            {r.cadence === 'monthly'
                              ? `/month · day ${r.day_of_month || 1}`
                              : r.cadence === 'weekly'
                              ? ` · ${(r.days_of_week || '').split(',').filter(Boolean).length || '?'}×/wk`
                              : r.cadence === 'geo' ? ' · when there' : '/day'}
                          </span>
                          <span className={`text-[10px] uppercase ${
                                  r.active ? 'text-vpurple' : 'text-slate-600'}`}>
                            {r.active ? '⟳ on' : 'paused'}
                          </span>
                          <span className="text-slate-600">›</span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
                </div>
              );
            })()}</MapBoundary>, document.body)}

            {editRule && createPortal(<MapBoundary>
              {/* THE EVENT OVERLAY — one card: the SHARED DetailCard for the
                 event's spend (same card as an expense), then event-only
                 settings — cadence, per-visit, change location, pause, stop. */}
              <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-[2px]
                              flex items-center justify-center p-4">
                <div className="card w-full max-w-md max-h-[88vh] overflow-y-auto">
                  <div className="flex items-start gap-3">
                    <h3 className="font-semibold text-sm flex-1">
                      ⟳ {editRule.item_title}
                      <span className="block text-xs text-slate-500 font-normal">
                        @ {editRule.place_name}
                      </span>
                    </h3>
                    <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                       hover:text-white shrink-0"
                            onClick={() => { setEditRule(null); setEditCount(false); }}>✕</button>
                  </div>
                  {/* Pass-3 fold-in: a map event is an expense with a place and a
                      cadence — NOT a different thing — so it opens the SHARED
                      DetailCard, the same card an expense uses (its own +/− spend,
                      the event stepper, and the logged list). The old Simple/Advanced
                      (erTab) stepper editor is gone; event-only controls (cadence,
                      per-visit, change location, pause, stop) stay below. */}
                  {(() => {
                    const eItem = (budget.items || []).find((x) => x.kind === 'expense'
                      && (x.title || '').toLowerCase()
                         === (editRule.item_title || '').toLowerCase());
                    if (!eItem) {
                      return (
                        <p className="text-xs text-slate-500 mt-3">
                          This standing event has no matching expense on this month — use the
                          event settings below, or add the expense to log against it.
                        </p>
                      );
                    }
                    return (
                      <div className="mt-3">
                        <DetailCard item={eItem} onChange={() => { onChanged(); reloadMonth(); }}
                                    categories={eItem.category ? [{ id: eItem.category, name: eItem.category_name || 'Category' }] : []}
                                    banks={eItem.bank ? [{ id: eItem.bank, name: eItem.bank_name || 'Bank' }] : []}
                                    onOpenMap={() => setEvMore(true)} showHeader={false} />
                      </div>
                    );
                  })()}
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-4 mb-1">Event settings</div>
                  <button type="button" className="text-xs text-slate-400 hover:text-white transition" onClick={() => setEvMore((v) => !v)}>{evMore ? 'Fewer options' : 'Change cadence, per-visit, location or tax'}</button>
                  {evMore && (<>
{(() => {
                    const rItem = (budget.items || []).find((x) => x.kind === 'expense'
                      && (x.title || '').toLowerCase()
                         === (editRule.item_title || '').toLowerCase());
                    const rUnit = unitOf(rItem);
                    // How this visit is charged: by formula UNITS (× the R{rate}
                    // rate) or by a plain MONEY amount. Inferred from what's set,
                    // then pinned by editRule.by once the user picks — so a fresh
                    // switch to Money shows the money field even before typing.
                    const by = editRule.by
                      || (Number(editRule.units) > 0 ? 'units'
                        : Number(editRule.amount) > 0 ? 'money'
                        : (rUnit > 0 ? 'units' : 'money'));
                    return (
                    <div className="mt-3 grid gap-3">
                      {/* 1) ADVANCED REPEAT — Daily (times a day), Weekly (which
                             days), Monthly (which date) or geo, with a plain count
                             summary. Same cadence the recurring editor uses. */}
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Advanced repeat · how often</div>
                        <div className="flex gap-1.5">
                          {[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly'], ['geo', 'When there']].map(([k, l]) => (
                            <button key={k}
                                    className={`flex-1 text-[11px] py-1.5 rounded-lg border ${
                                      editRule.cadence === k ? 'border-vblue bg-vblue/15 text-white'
                                                             : 'border-edge text-slate-500'}`}
                                    onClick={() => setEditRule({ ...editRule, cadence: k })}>{l}</button>
                          ))}
                        </div>
                        {editRule.cadence === 'daily' && (
                          <label className="block mt-2">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500">Times a day — 1 is once a day</span>
                            <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="1" max="12"
                                   placeholder="e.g. 2" value={editRule.times_per_day}
                                   onChange={(e) => setEditRule({ ...editRule, times_per_day: e.target.value })} />
                          </label>
                        )}
                        {editRule.cadence === 'weekly' && (() => {
                          const days = (editRule.days_of_week || '').split(',').filter(Boolean);
                          const toggle = (d) => {
                            const nx = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
                            setEditRule({ ...editRule, days_of_week: nx.sort().join(',') });
                          };
                          return (
                            <div className="mt-2">
                              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                Which days? · {days.length} a week
                              </span>
                              <div className="flex gap-1 mt-1">
                                {[['1', 'M'], ['2', 'T'], ['3', 'W'], ['4', 'T'], ['5', 'F'], ['6', 'S'], ['7', 'S']].map(([d, l], i) => (
                                  <button key={i} type="button"
                                          className={`w-8 h-8 rounded-lg border text-xs ${
                                            days.includes(d) ? 'border-vblue text-vblue bg-vblue/10' : 'border-edge text-slate-500'}`}
                                          onClick={() => toggle(d)}>{l}</button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        {editRule.cadence === 'monthly' && (
                          <label className="block mt-2">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500">Which day of the month (1–28)</span>
                            <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="1" max="28"
                                   placeholder="1–28" value={editRule.day_of_month}
                                   onChange={(e) => setEditRule({ ...editRule, day_of_month: e.target.value })} />
                          </label>
                        )}
                        {(() => {
                          const c = editRule.cadence;
                          const n = (editRule.days_of_week || '').split(',').filter(Boolean).length;
                          const t = Math.max(1, Number(editRule.times_per_day) || 1);
                          let msg = '';
                          if (c === 'daily') msg = (t === 1 ? 'Once a day' : `${t}× a day`) + ` → about ${t * 31} logs this month.`;
                          else if (c === 'weekly') msg = n ? ((n === 1 ? 'Once a week' : `${n} days a week`) + ` → about ${Math.round(n * 4.43)} logs this month.`) : 'Pick which days.';
                          else if (c === 'monthly') msg = `Once a month on day ${editRule.day_of_month || '—'} → 1 log this month.`;
                          return msg ? <p className="text-[10px] text-slate-500 mt-1.5">{msg}</p> : null;
                        })()}
                      </div>

                      {/* 2) WHAT EACH VISIT LOGS — units (× rate) OR a money amount.
                             Switching modes keeps both values; only the active one is saved. */}
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">What each visit logs</div>
                        {rUnit > 0 && (
                          <div className="flex gap-1.5 mb-2">
                            {[['units', `Units · ${moneySymbol()}${rUnit} each`], ['money', `Money (${moneySymbol()})`]].map(([m, l]) => (
                              <button key={m}
                                      className={`flex-1 text-[11px] py-1.5 rounded-lg border ${
                                        by === m ? 'border-vblue bg-vblue/15 text-white'
                                                 : 'border-edge text-slate-500'}`}
                                      onClick={() => setEditRule({ ...editRule, by: m,
                                        ...(m === 'money' ? { units: '' } : {}) })}>{l}</button>
                            ))}
                          </div>
                        )}
                        {(rUnit > 0 && by === 'units') ? (
                          <div className="grid gap-2">
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wide text-slate-500">Units per visit</span>
                              <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="0"
                                     placeholder="e.g. 1" value={editRule.units}
                                     onChange={(e) => setEditRule({ ...editRule, by: 'units', units: e.target.value })} />
                            </label>
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                Amount per unit ({moneySymbol()}) — blank uses the expense’s {moneySymbol()}{rUnit}
                              </span>
                              <input className="input !py-1.5 text-sm w-full mt-0.5"
                                     placeholder={`R${rUnit} (expense default)`} value={editRule.amount}
                                     onChange={(e) => setEditRule({ ...editRule, by: 'units', amount: e.target.value })} />
                            </label>
                          </div>
                        ) : (
                          <label className="block">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500">Money per visit ({moneySymbol()})</span>
                            <input className="input !py-1.5 text-sm w-full mt-0.5"
                                   placeholder="e.g. 37.00" value={editRule.amount}
                                   onChange={(e) => setEditRule({ ...editRule, by: 'money', amount: e.target.value })} />
                          </label>
                        )}
                        {(() => {
                          const u = Number(editRule.units) || 0;
                          const a = Number(editRule.amount) || 0;
                          const useUnits = by === 'units' && rUnit > 0;
                          const rate = (by === 'units' && Number(editRule.amount) > 0) ? Number(editRule.amount) : rUnit;
                          const per = useUnits ? u * rate : a;
                          if (!per) return null;
                          const times = editRule.cadence === 'daily' ? Math.max(1, Number(editRule.times_per_day) || 1) : 1;
                          const perLbl = useUnits ? `${u}× unit${u === 1 ? '' : 's'} (${fmtR(u * rate)})` : fmtR(a);
                          const when = editRule.cadence === 'monthly' ? ` on day ${editRule.day_of_month || 1}`
                            : editRule.cadence === 'geo' ? ' when you’re there' : '';
                          return (
                            <p className="text-[11px] text-emerald-400 mt-2">
                              Logs {perLbl}{times > 1 ? ` × ${times}/day = ${fmtR(per * times)}/day` : `${when}`}
                              {times > 1 ? when : ''}.
                            </p>
                          );
                        })()}
                      </div>

                      <div className="border-t border-edge pt-2 mt-1">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Move to another shop
                        </div>
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5">
                        <input className="input !py-1.5 text-sm flex-1"
                               placeholder="🔎 Find the new shop…"
                               value={erQ} onChange={(e) => setErQ(e.target.value)} />
                        <VoiceButton onText={(t) => setErQ(t)} title="Find the shop by voice" />
                      </div>
                      <div className="grid gap-1 mt-1.5 max-h-40 overflow-y-auto">
                        {places
                          .filter((p) => !erQ.trim()
                            || p.name.toLowerCase().includes(erQ.trim().toLowerCase()))
                          .map((p) => (
                            <button key={p.id}
                                    className="text-left border border-edge rounded-lg
                                               px-2.5 py-1.5 text-sm hover:border-vblue
                                               transition"
                                    onClick={async () => {
                                      const up = await api(
                                        `/budget/place-visit-rules/${editRule.id}/`,
                                        { method: 'PATCH', body: { place: p.id } });
                                      setRules((rs) => rs.map((x) =>
                                        (x.id === editRule.id ? up : x)));
                                      setEditRule({ ...editRule,
                                        place_name: up.place_name });
                                      setErQ('');
                                    }}>
                              📍 {p.name}
                            </button>
                          ))}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">
                        Picking a shop moves the event there from its next run —
                        past days keep their original shop.
                      </p>
                    </div>
                      </div>
                      <p className="text-[10px] text-slate-600">
                        Save applies these everywhere the event shows.
                      </p>
                    </div>
                    );
                  })()}
{taxPackAvailable() && (() => {
                    const rItem = (budget.items || []).find((x) => x.kind === 'expense'
                      && (x.title || '').toLowerCase() === (editRule.item_title || '').toLowerCase());
                    if (!rItem) return (
                      <p className="text-xs text-slate-500 mt-3">
                        This event follows its expense. Open the expense from your list
                        to set its tax details - they apply here too.
                      </p>
                    );
                    return (
                      <div className="mt-3 space-y-3" data-guide="tax-panel">
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Flag &ldquo;{rItem.title}&rdquo; for your SARS tax pack and pick a bucket.
                          Organises what you logged in Rands.
                        </p>
                        <label className="flex items-center gap-2 text-sm" data-guide="tax-deductible">
                          <input type="checkbox" defaultChecked={!!rItem.tax_deductible}
                                 onChange={async (e) => {
                                   await api(`/budget/items/${rItem.id}/`,
                                             { method: 'PATCH', body: { tax_deductible: e.target.checked } });
                                   onChanged();
                                 }} />
                          Tax-deductible
                        </label>
                        <div data-guide="tax-bucket">
                          <label className="block text-[10px] text-slate-500 mb-1">SARS bucket</label>
                          <select className="input !py-1.5 text-sm" defaultValue={rItem.tax_bucket || ''}
                                  onChange={async (e) => {
                                    await api(`/budget/items/${rItem.id}/`,
                                              { method: 'PATCH', body: { tax_bucket: e.target.value } });
                                    onChanged();
                                  }}>
                            <option value="">-</option>
                            <option value="travel">Travel</option>
                            <option value="home_office">Home office</option>
                            <option value="donations">Donations</option>
                            <option value="medical">Medical</option>
                            <option value="retirement">Retirement</option>
                            <option value="business">Business</option>
                          </select>
                        </div>
                      </div>
                    );
                  })()}
                  </>)}

                  <div className="grid gap-1.5 mt-3">
                    <button className="btn-primary !py-2 text-sm"
                            onClick={async () => {
                              // units>0 → units event (amount = optional per-unit
                              // override, 0 = use the expense's own rate). units==0 →
                              // flat money event (amount = money per visit).
                              const up = await api(
                                `/budget/place-visit-rules/${editRule.id}/`,
                                { method: 'PATCH',
                                  body: { units: Number(editRule.units) || 0,
                                          amount: Number(editRule.amount) || 0,
                                          cadence: editRule.cadence,
                                          times_per_day: editRule.cadence === 'daily'
                                            ? Math.max(1, Number(editRule.times_per_day) || 1)
                                            : 1,
                                          day_of_month: editRule.cadence === 'monthly'
                                            ? Math.min(Number(editRule.day_of_month) || 1, 28)
                                            : null,
                                          days_of_week: editRule.cadence === 'weekly'
                                            ? (editRule.days_of_week || '')
                                            : '' } });
                              setRules((rs) => rs.map((x) =>
                                (x.id === editRule.id ? up : x)));
                              setEditRule(null);
                            }}>
                      Save
                    </button>
                    <button className={`py-2 rounded-xl border text-sm ${
                              editRule.active
                                ? 'border-edge text-slate-400 hover:border-vpurple'
                                : 'border-vpurple text-vpurple'}`}
                            onClick={async () => {
                              const up = await api(
                                `/budget/place-visit-rules/${editRule.id}/`,
                                { method: 'PATCH',
                                  body: { active: !editRule.active } });
                              setRules((rs) => rs.map((x) =>
                                (x.id === editRule.id ? up : x)));
                              setEditRule({ ...editRule, active: up.active });
                            }}>
                      {editRule.active ? '⏸ Pause the event' : '⟳ Resume the event'}
                    </button>
                    <button className="py-2 rounded-xl border border-red-400/50 text-red-400
                                       text-sm hover:bg-red-400/10"
                            onClick={async () => {
                              if (await ask({
                                title: `Stop "${editRule.item_title}"?`,
                                message: 'The standing event goes; already-logged days '
                                  + 'stay and remain deletable one by one.',
                                ok: 'Stop it', danger: true, cancel: 'Keep it',
                              })) {
                                await api(`/budget/place-visit-rules/${editRule.id}/`,
                                          { method: 'DELETE' });
                                // If the shop this event lived at now has NOTHING else
                                // pointing to it — no other event, no homed expense, no
                                // logged visit — clean up the orphaned pin/shop too.
                                const pid = editRule.place;
                                const otherRule = rules.some((r) => r.id !== editRule.id && r.place === pid);
                                const homed = expenses.some((e) => e.place === pid);
                                const visited = visits.some((v) => v.place === pid);
                                if (pid && !otherRule && !homed && !visited) {
                                  await api(`/budget/places/${pid}/`, { method: 'DELETE' }).catch(() => {});
                                  setPlaces((ps) => ps.filter((p) => p.id !== pid));
                                }
                                setRules((rs) => rs.filter((x) => x.id !== editRule.id));
                                setEditRule(null);
                              }
                            }}>
                      ✕ Stop it for good
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2">
                    This event records{' '}
                    {editRule.times_per_day > 1 ? `${editRule.times_per_day}× ` : ''}
                    {editRule.units ? `${editRule.units}× unit${
                      Number(editRule.units) === 1 ? '' : 's'}` : `R${editRule.amount}`}
                    {' '}for “{editRule.item_title}”{' '}
                    {editRule.cadence === 'daily' ? 'at the end of each day (±23:30)'
                      : editRule.cadence === 'monthly'
                        ? `monthly on day ${editRule.day_of_month || 1}`
                        : 'only when you’re there and approve'} as PENDING
                    visit(s) at {editRule.place_name} — WhatsApp asks you to
                    confirm each; nothing is added until you do. Fix any single
                    day with the expense's − / + or by deleting that entry.
                  </p>
                </div>
              </div>
            </MapBoundary>, document.body)}

            {!byDate && !places.length && !unplaced.length && (
              <p className="text-slate-500 text-sm mt-6">
                Log some expenses first — then place them here and watch the map fill up.
              </p>
            )}
          </>
        )}
        <p className="text-[10px] text-slate-600 mt-6 pt-3 border-t border-edge">
          {budget.month} · {places.length} place{places.length === 1 ? '' : 's'} ·{' '}
          {unplaced.length} not placed ·{' '}
          <span title="The exact code this screen is running">
            build {typeof __BUILD__ !== 'undefined' ? __BUILD__ : 'dev'}
          </span>
        </p>
        </MapBoundary>
      </div>
      {cashPlace != null && (
        <CashSpend items={expenses} place={cashPlace}
                   onClose={() => setCashPlace(null)}
                   onDone={() => { setCashPlace(null); onChanged(); }} />
      )}
    </div>
  );
}
